import { InvocaPhoneNumber } from "@src/common/InvocaPhoneNumber";
import { APP_ROUTES, BOTTOM_NAV_MOBILE_CSS, CARE_FORMAT, PHONE_NUMBER } from "@src/constants";
import { CLINICIAN_TYPE, MODALITY_LABEL } from "@src/constants/filter-constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { PROVIDER_SORT_OPTIONS } from "@src/constants/provider-search-sort.constants";
import { useApplicationContext, useFeatureExposure } from "@src/context";
import { useEnvironment } from "@src/context/EnvironmentContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { useFilterContext } from "@src/context/FilterContext";
import { useProviderListContext } from "@src/context/ProviderListContext";
import { useThemeContext } from "@src/context/ThemeContext";
import { useTimerContext } from "@src/context/TimerContext";
import { useExclusiveAction } from "@src/hooks/useExclusiveAction";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import { useReleaseFrozenAppointmentOnMount } from "@src/hooks/useReleaseFrozenAppointmentOnMount";
import { useResolvedPageLabels } from "@src/hooks/useResolvedPageLabels";
import { useThemePageText } from "@src/hooks/useThemePageText";
import type {
  Provider,
  ProviderDepartment,
  ProviderSlot,
} from "@src/interfaces/provider.interface";
import type { ProviderPhoto, ProviderPhotoKey } from "@src/interfaces/provider-photo.interface";
import { getInitialPatientInformationPath } from "@src/routes/PatientInformation/patientInformationNavigation";
import {
  freezeAppointmentSlots,
  getProviderAvailableDates,
  getProviderAvailableSlots,
} from "@src/services";
import ClientLogsService from "@src/services/client-logs.service";
import { getProviderAvailabilityBatch } from "@src/services/provider-availability-batch.service";
import {
  getCachedProviderPhotosByProviderKey,
  getProviderPhotos,
  mapProviderPhotosByProviderKey,
} from "@src/services/provider-photos.service";
import {
  buildProviderSearchRequest,
  getProviderList,
  getProviderSearchSortOverrides,
} from "@src/services/provider-search.service";
import { asyncPool } from "@src/utils/async-pool";
import { getMinimumAppointmentMoment } from "@src/utils/date-utils";
import {
  type DepartmentOption,
  generateDepartmentOptions,
} from "@src/utils/department-options-utils";
import { mapProviderList } from "@src/utils/mapping-utils";
import {
  getCachedProviderDates,
  getCachedProviderDatesError,
  getCachedProviderSlots,
  setCachedProviderDates,
  setCachedProviderDatesError,
  setCachedProviderSlots,
} from "@src/utils/provider-availability-cache";
import {
  filterIsoDatesBySelectedWeekdays,
  filterSlotsByHourRange,
} from "@src/utils/provider-availability-display-filters";
import {
  filterProvidersByRequestedModality,
  orderVirtualOnlyProvidersLast,
} from "@src/utils/provider-modality-filter";
import { buildProviderPhotoKey, getProviderPhotoMetadata } from "@src/utils/provider-photo-utils";
import { renderPhoneTemplateText } from "@src/utils/render-phone-template-text";
import { buildSchedulingMetadata } from "@src/utils/scheduling-metadata";
import {
  hasMixedSessionTypes,
  resolveModalityFromSessionType,
} from "@src/utils/session-type-modality";
import { isTestRecordBlocked, TEST_RECORD_BLOCK_MESSAGE } from "@src/utils/test-record-utils";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { usePagination } from "@uhg-abyss/web/hooks/usePagination";
import { dayjs } from "@uhg-abyss/web/tools/dayjs";
import { useLocation } from "@uhg-abyss/web/tools/reactRouterTools";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Card } from "@uhg-abyss/web/ui/Card";
import { Divider } from "@uhg-abyss/web/ui/Divider";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { LoadingSpinner } from "@uhg-abyss/web/ui/LoadingSpinner";
import { Pagination } from "@uhg-abyss/web/ui/Pagination";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { Text } from "@uhg-abyss/web/ui/Text";
import moment from "moment-timezone";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getClinicNameForDepartmentScope,
  partitionProvidersByGroupAndDepartment,
} from "../utils/provider-partition-utils";
import { ActiveFilters } from "./ActiveFilters";
import { ExpandProvidersRow } from "./ExpandProvidersRow";
import { LocationSelectionModal } from "./LocationSelectionModal";
import type { AvailabilitySelection } from "./ProviderAvailabilityPanel";
import { ProviderCard } from "./ProviderCard";
import { SearchAgain } from "./SearchAgain";

const getSortSelectCss = (isMobile: boolean) =>
  ({
    "abyss-select-input-container": {
      borderColor: "$app.color.find.provider.dropdown.border",
      color: "$app.color.find.provider.dropdown.text",
      minWidth: isMobile ? "auto" : "250px",
      width: isMobile ? "100%" : "auto",
    },
    "abyss-select-input-wrapper": {
      gap: "0",
    },
    "abyss-select-input-menu-expand-icon": {
      color: "$app.color.find.provider.dropdown.icon !important",
    },
  }) as const;

const LoadingSpinnerContainer = styled("div", {
  width: "100%",
  height: "100%",
  padding: "40px 16px",
  justifyContent: "center",
  gap: "8px",
  display: "inline-flex",
});

const LoadingTextContainer = styled("div", {
  paddingTop: "5px",
  paddingLeft: "10px",
  width: "200px",
  display: "flex",
  flexDirection: "column",
});

const BoldPhoneSpan = styled("span", {
  fontWeight: 700,
});

const renderBoldPhoneNumber = (phoneNumber: string) => {
  return (
    <BoldPhoneSpan>
      <InvocaPhoneNumber phoneNumber={phoneNumber} />
    </BoldPhoneSpan>
  );
};

const WEBFORM_ELIGIBLE_ROLES = new Set(["patient", "referrer"]);

const getValidHttpsUrl = (url: string): string | null => {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return null;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    return parsedUrl.protocol === "https:" ? parsedUrl.toString() : null;
  } catch {
    return null;
  }
};

const NoResultsContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  marginTop: "105px",
  padding: "58px 0",
  variants: {
    isMobile: {
      true: {
        marginTop: "0",
        padding: "0 0 16px",
      },
    },
  },
});

const FewResultsContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignSelf: "center !important",
  width: "100%",
  padding: "0",
  alignItems: "center",
  variants: {
    isMobile: {
      true: {
        padding: "0 0 16px",
      },
    },
  },
});

const SubTextContainer = styled("div", {
  lineHeight: "20px",
  gap: "8px",
  display: "flex",
  flexDirection: "column",
  variants: {
    isMobile: {
      true: {
        lineHeight: "18px",
      },
    },
  },
});

const ResultSubText = styled(Text, {
  variants: {
    isMobile: {
      true: {
        fontSize: "14px !important",
      },
    },
  },
});

const ResultSubParagraph = styled("p", {
  variants: {
    isMobile: {
      true: {
        fontSize: "14px !important",
      },
    },
  },
});

const ResultsHeaderRow = styled("div", {
  width: "100%",
  display: "flex",
  flexDirection: "row",
  gap: "16px",
  justifyContent: "space-between",
  variants: {
    isMobile: {
      true: {
        flexDirection: "column",
      },
    },
  },
});

const SortDropdownHolder = styled("div", {
  display: "flex",
  order: 2,
  marginTop: "0",
  alignItems: "center",
  gap: "5px",
  variants: {
    isMobile: {
      true: {
        order: 1,
        marginTop: "16px",
        width: "100%",
      },
    },
  },
});

const SortLabelText = styled(Text, {
  whiteSpace: "nowrap",
  variants: {
    isMobile: {
      true: {
        display: "none",
      },
      false: {
        display: "block",
      },
    },
  },
});

const ProviderCardsHolder = styled("div", {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: "24px",
  paddingBottom: "24px",
  variants: {
    isMobile: {
      true: {
        gap: "16px",
      },
    },
    isSchedulingConsolidationEnabled: {
      true: {
        gap: "30px",
        paddingBottom: "30px",
      },
    },
  },
  compoundVariants: [
    {
      isMobile: true,
      isSchedulingConsolidationEnabled: true,
      css: {
        gap: "20px",
      },
    },
  ],
});

const BackButtonHolder = styled("div", {
  marginBottom: "40px",
  justifyContent: "flex-start",
  width: "100%",
  alignSelf: "center",
  variants: {
    isMobile: {
      true: {
        ...BOTTOM_NAV_MOBILE_CSS,
        display: "flex",
        justifyContent: "center",
        marginBottom: "0px",
      },
    },
  },
});

const AvailabilityToolbarContainer = styled("div", {
  width: "100%",
  padding: "0 0 4px",
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "-12px",
});

const GlobalAvailabilityPager = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const GlobalAvailabilityPagerButton = styled("button", {
  border: "none",
  backgroundColor: "transparent",
  color: "$app.color.text.primary",
  width: "30px",
  height: "30px",
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: "6px",
  "&:disabled": {
    opacity: 0.35,
    cursor: "not-allowed",
  },
});

const AVAILABILITY_PAGER_DEBOUNCE_MS = 300;

type AvailabilityRequestState = {
  isLoadingDates: boolean;
  datesLoaded: boolean;
  datesRequestSignature: string;
  // Signature of the last batch-availability attempt for this provider, win or lose —
  // prevents the batch effect from retrying the same signature indefinitely after an
  // error result; a new signature (filters/context changed) clears this naturally.
  batchAttemptedSignature: string;
  isLoadingSlots: boolean;
  errorMessage?: string;
  availableDateSet: Set<string>;
  windowStartIsoDate: string;
  dateWindowOffset: number;
  slotsByIsoDate: Record<string, ProviderSlot[]>;
  expandedDateSet: Set<string>;
  selectedSlotKey: string | null;
  selectedSlot?: {
    isoDate: string;
    slot: ProviderSlot;
    modalityLabel: string;
  };
};

type LocationPromptState = {
  provider: Provider;
  selectedSlot: { isoDate: string; slot: ProviderSlot; modalityLabel: string };
  options: DepartmentOption[];
  initialValue: string;
} | null;

type ProviderAvailabilityStatus = "loading" | "error" | "complete";

type ModalityPreference = "telehealth" | "in-person" | "no preference";

const PROVIDER_SORT_SELECT_OPTIONS = [
  {
    value: PROVIDER_SORT_OPTIONS.RECOMMENDED,
    label: PROVIDER_SORT_OPTIONS.RECOMMENDED,
  },
  {
    value: PROVIDER_SORT_OPTIONS.NEAREST_TO_FURTHEST,
    label: PROVIDER_SORT_OPTIONS.NEAREST_TO_FURTHEST,
  },
  { value: PROVIDER_SORT_OPTIONS.NAME_ASC, label: PROVIDER_SORT_OPTIONS.NAME_ASC },
  { value: PROVIDER_SORT_OPTIONS.NAME_DESC, label: PROVIDER_SORT_OPTIONS.NAME_DESC },
];

const getProviderSortSelectOptions = (modalityPreference: ModalityPreference) => {
  return PROVIDER_SORT_SELECT_OPTIONS.map((option) => ({
    ...option,
    ...(option.value === PROVIDER_SORT_OPTIONS.NEAREST_TO_FURTHEST
      ? { isDisabled: modalityPreference === "telehealth" }
      : {}),
  }));
};

const shouldRefreshProviderSearchForSort = (selectedSortBy: string): boolean => {
  return (
    selectedSortBy === PROVIDER_SORT_OPTIONS.NEAREST_TO_FURTHEST ||
    selectedSortBy === PROVIDER_SORT_OPTIONS.RECOMMENDED
  );
};

const buildDefaultAvailabilityState = (): AvailabilityRequestState => {
  return {
    isLoadingDates: false,
    datesLoaded: false,
    datesRequestSignature: "",
    batchAttemptedSignature: "",
    isLoadingSlots: false,
    errorMessage: undefined,
    availableDateSet: new Set<string>(),
    windowStartIsoDate: dayjs().format("YYYY-MM-DD"),
    dateWindowOffset: 0,
    slotsByIsoDate: {},
    expandedDateSet: new Set<string>(),
    selectedSlotKey: null,
    selectedSlot: undefined,
  };
};

const formatIsoDateAsUs = (isoDate: string): string => {
  return dayjs(isoDate).format("MM/DD/YYYY");
};

const buildAvailabilitySlotKey = (
  providerId: string,
  isoDate: string,
  slot: ProviderSlot,
): string => {
  return `${providerId}:${isoDate}:${slot.start_time}:${slot.department_id}:${slot.appointment_type_id ?? ""}:${slot.appointment_ids.join("-")}`;
};

const getProviderDepartmentIds = (provider: Provider): number[] => {
  return (provider.departments || [])
    .map((department) => {
      return department.department_id;
    })
    .filter((departmentId) => {
      return Number.isFinite(departmentId);
    });
};

const sortIsoDates = (isoDates: string[]): string[] => {
  return Array.from(isoDates).sort((first, second) => {
    return first.localeCompare(second);
  });
};

const getSchedulableIsoDates = (
  isoDates: string[],
  minimumSchedulableIsoDate: string,
): string[] => {
  const schedulableDates: string[] = [];
  for (const isoDate of isoDates) {
    if (isoDate >= minimumSchedulableIsoDate) {
      schedulableDates.push(isoDate);
    }
  }
  return sortIsoDates(schedulableDates);
};

const getNonEmptySlotIsoDates = (slotsByIsoDate: Record<string, ProviderSlot[]>): string[] => {
  const nonEmptySlotIsoDates: string[] = [];
  for (const [isoDate, slots] of Object.entries(slotsByIsoDate)) {
    if (slots.length > 0) {
      nonEmptySlotIsoDates.push(isoDate);
    }
  }
  return [...nonEmptySlotIsoDates].sort((first, second) => {
    return first.localeCompare(second);
  });
};

const sortSlotsByTime = (slots: ProviderSlot[]): ProviderSlot[] => {
  return [...slots].sort((first, second) => {
    return first.start_time.localeCompare(second.start_time);
  });
};

const isDateAfter = (candidateIsoDate: string, currentIsoDate: string): boolean => {
  return candidateIsoDate.localeCompare(currentIsoDate) > 0;
};

const formatAppointmentTime = (time24h: string): string => {
  const [hoursRaw, minutesRaw] = time24h.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return time24h;
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
};

const getCareFormatsForPreference = (
  modalityPreference: ModalityPreference,
): Array<"telehealth" | "in-person"> => {
  if (modalityPreference === "no preference") {
    return ["telehealth", "in-person"];
  }

  return [modalityPreference];
};

interface BuildProviderSlotTaskInput {
  provider: Provider;
  isoDate: string;
  modalityPreference: ModalityPreference;
  practiceId: number;
  providerNumberId: number;
  departmentIds: number[];
  userUuid: string;
  latitude: number;
  longitude: number;
  durationMinutes: number;
  startHour?: number;
  endHour?: number;
  cacheScope: string;
  minimumDayTime?: string;
}

interface BuildProviderDatesTaskInput {
  provider: Provider;
  practiceId: number;
  startDate: string;
  currentMoment: moment.Moment;
  minimumAppointmentDateTime: moment.Moment;
  modalityPreference: ModalityPreference;
  ageRange?: string;
  userUuid?: string;
  userTimezone?: string;
  cacheScope: string;
}

const buildProviderDatesCacheScope = ({
  practiceId,
  departmentIds,
  startDate,
  endDate,
  minimumSchedulableIsoDate,
  durationMinutes,
  clinicianType,
  userTimezone,
}: {
  practiceId: number;
  departmentIds: number[];
  startDate: string;
  endDate: string;
  minimumSchedulableIsoDate: string;
  durationMinutes: number;
  clinicianType: string;
  userTimezone?: string;
}): string => {
  const departmentScope = [...departmentIds].sort((first, second) => {
    return first - second;
  });

  return JSON.stringify({
    practiceId,
    departmentIds: departmentScope,
    startDate,
    endDate,
    minimumSchedulableIsoDate,
    durationMinutes,
    clinicianType,
    userTimezone: userTimezone || "",
  });
};

const buildProviderSlotsCacheScope = ({
  practiceId,
  departmentIds,
  durationMinutes,
  clinicianType,
  latitude,
  longitude,
  minimumDayTime,
}: {
  practiceId: number;
  departmentIds: number[];
  durationMinutes: number;
  clinicianType: string;
  latitude: number;
  longitude: number;
  minimumDayTime?: string;
}): string => {
  const departmentScope = [...departmentIds].sort((first, second) => {
    return first - second;
  });

  return JSON.stringify({
    practiceId,
    departmentIds: departmentScope,
    durationMinutes,
    clinicianType,
    latitude,
    longitude,
    minimumDayTime: minimumDayTime || "",
  });
};

const logProviderDatesTaskOutcome = ({
  userUuid,
  providerId,
  minimumSchedulableIsoDate,
  result,
}: {
  userUuid?: string;
  providerId: string;
  minimumSchedulableIsoDate: string;
  result:
    | { status: "rejected"; reason: unknown }
    | {
        status: "fulfilled";
        value: { providerId: string; isoDates: string[]; errorMessage?: string };
      };
}): void => {
  if (result.status === "rejected") {
    ClientLogsService.logError(
      userUuid || "unknown",
      "ProviderList.loadAvailabilityDates",
      `Provider ${providerId}: Dates task rejected`,
      result.reason instanceof Error ? result.reason : new Error(String(result.reason)),
    );
    return;
  }

  if (result.value.errorMessage) {
    return;
  }

  const schedulableIsoDates = getSchedulableIsoDates(
    result.value.isoDates,
    minimumSchedulableIsoDate,
  );
  if (schedulableIsoDates.length === 0 && result.value.isoDates.length > 0) {
    ClientLogsService.logWebAction(
      userUuid || "unknown",
      "ProviderList.loadAvailabilityDates",
      "Provider availability dates filtered out",
      {
        providerId,
        minimumSchedulableIsoDate,
        receivedDates: result.value.isoDates,
      },
      {
        level: "warn",
        text: `Provider ${providerId}: Received dates but all were filtered out`,
        location: "ProviderList.loadAvailabilityDates",
      },
    );
  }
};

const loadProviderDatesForCareFormat = async ({
  providerId,
  providerNumberId,
  careFormat,
  cacheScope,
  practiceId,
  departmentIds,
  startDate,
  endDate,
  minimumAppointmentDateTime,
  durationMinutes,
  clinicianType,
  userUuid,
  userTimezone,
}: {
  providerId: string;
  providerNumberId: number;
  careFormat: "telehealth" | "in-person";
  cacheScope: string;
  practiceId: number;
  departmentIds: number[];
  startDate: string;
  endDate: string;
  minimumAppointmentDateTime: moment.Moment;
  durationMinutes: number;
  clinicianType: string;
  userUuid?: string;
  userTimezone?: string;
}): Promise<{ isoDates: string[]; errorMessage?: string; wasSuccessful: boolean }> => {
  const cachedDates = getCachedProviderDates(providerId, careFormat, cacheScope);
  if (cachedDates) {
    return {
      isoDates: cachedDates,
      errorMessage: undefined,
      wasSuccessful: true,
    };
  }

  const cachedDatesError = getCachedProviderDatesError(providerId, careFormat, cacheScope);
  if (cachedDatesError) {
    return {
      isoDates: [],
      errorMessage: cachedDatesError,
      wasSuccessful: false,
    };
  }

  const response = await getProviderAvailableDates({
    user_uuid: userUuid || "",
    practiceId,
    departmentIds,
    providerId: providerNumberId,
    startDate,
    endDate,
    careFormat,
    user_timezone: userTimezone || "",
    minimum_day_time: minimumAppointmentDateTime.format("YYYY-MM-DDTHH:mm:ssZ"),
    duration: durationMinutes,
    clinicianType,
  });

  if (!response.success || !response.data) {
    const errorMessage =
      typeof response.error === "string"
        ? response.error
        : "Failed to load provider availability dates.";

    setCachedProviderDatesError(providerId, careFormat, cacheScope, errorMessage);

    return {
      isoDates: [],
      errorMessage,
      wasSuccessful: false,
    };
  }

  setCachedProviderDates(providerId, careFormat, cacheScope, response.data.availableDates);

  return {
    isoDates: response.data.availableDates,
    errorMessage: undefined,
    wasSuccessful: true,
  };
};

const buildProviderDatesTask = ({
  provider,
  practiceId,
  startDate,
  currentMoment,
  minimumAppointmentDateTime,
  modalityPreference,
  ageRange,
  userUuid,
  userTimezone,
  cacheScope,
}: BuildProviderDatesTaskInput): (() => Promise<{
  providerId: string;
  isoDates: string[];
  errorMessage?: string;
}>) => {
  return async () => {
    const providerId = provider.providerId;
    const providerNumberId = Number(provider.providerId);

    if (!Number.isFinite(providerNumberId)) {
      return { providerId, isoDates: [] as string[], errorMessage: "Provider ID is invalid." };
    }

    const departmentIds = getProviderDepartmentIds(provider);
    if (departmentIds.length === 0) {
      return { providerId, isoDates: [] as string[], errorMessage: undefined };
    }

    const schedulingMetadata = buildSchedulingMetadata({
      ageRange,
      clinicianType: provider.clinicianType,
    });

    const endDateBase =
      provider.clinicianType === CLINICIAN_TYPE.PRESCRIBER
        ? currentMoment.clone().startOf("day")
        : minimumAppointmentDateTime.clone().startOf("day");
    const endDate = endDateBase
      .add(Math.min(30, schedulingMetadata.schedulingWindowDays), "days")
      .format("MM/DD/YYYY");

    const careFormats = getCareFormatsForPreference(modalityPreference);
    const allIsoDates: string[] = [];
    let hasAtLeastOneSuccessfulResponse = false;
    let firstErrorMessage: string | undefined;

    for (const careFormat of careFormats) {
      const result = await loadProviderDatesForCareFormat({
        providerId,
        providerNumberId,
        careFormat,
        cacheScope,
        practiceId,
        departmentIds,
        startDate,
        endDate,
        minimumAppointmentDateTime,
        durationMinutes: schedulingMetadata.durationMinutes,
        clinicianType: provider.clinicianType,
        userUuid,
        userTimezone,
      });

      if (result.wasSuccessful) {
        allIsoDates.push(...result.isoDates);
        hasAtLeastOneSuccessfulResponse = true;
      } else if (!firstErrorMessage && result.errorMessage) {
        firstErrorMessage = result.errorMessage;
      }
    }

    if (!hasAtLeastOneSuccessfulResponse) {
      return {
        providerId,
        isoDates: [] as string[],
        errorMessage: firstErrorMessage || "Failed to load provider availability dates.",
      };
    }

    return {
      providerId,
      isoDates: sortIsoDates(Array.from(new Set(allIsoDates))),
      errorMessage: undefined,
    };
  };
};

interface ExecuteProviderSlotTaskInput {
  task: () => Promise<{ providerId: string; isoDate: string; slots: ProviderSlot[] }>;
  slotRequestKey: string;
  providerId: string;
  isoDate: string;
  slotRequestGeneration: number;
  slotRequestGenerationRef: { current: number };
  inFlightSlotRequestsRef: { current: Set<string> };
  pendingSlotCountByProvider: Map<string, number>;
  setAvailabilityByProvider: (
    updater: (
      current: Record<string, AvailabilityRequestState>,
    ) => Record<string, AvailabilityRequestState>,
  ) => void;
  userUuid?: string;
}

interface SlotTaskDescriptor {
  task: () => Promise<{ providerId: string; isoDate: string; slots: ProviderSlot[] }>;
  slotRequestKey: string;
  providerId: string;
  isoDate: string;
}

interface BuildProviderSlotTaskPlanInput {
  visibleProviders: Provider[];
  availabilityByProvider: Record<string, AvailabilityRequestState>;
  minimumSchedulableIsoDate: string;
  minimumDayTime: string;
  startHour?: number;
  endHour?: number;
  modalityPreference: ModalityPreference;
  practiceId: number;
  ageRange?: string;
  userUuid?: string;
  normalizedLatitude: number;
  normalizedLongitude: number;
  getVisibleIsoDatesForProvider: (providerId: string) => string[];
  inFlightSlotRequestsRef: { current: Set<string> };
}

const appendProviderSlotTaskDescriptors = ({
  provider,
  providerState,
  minimumSchedulableIsoDate,
  minimumDayTime,
  startHour,
  endHour,
  modalityPreference,
  practiceId,
  ageRange,
  userUuid,
  normalizedLatitude,
  normalizedLongitude,
  getVisibleIsoDatesForProvider,
  inFlightSlotRequestsRef,
  descriptors,
  providersWithPendingSlots,
  pendingSlotCountByProvider,
}: {
  provider: Provider;
  providerState: AvailabilityRequestState;
  minimumSchedulableIsoDate: string;
  minimumDayTime: string;
  startHour?: number;
  endHour?: number;
  modalityPreference: ModalityPreference;
  practiceId: number;
  ageRange?: string;
  userUuid?: string;
  normalizedLatitude: number;
  normalizedLongitude: number;
  getVisibleIsoDatesForProvider: (providerId: string) => string[];
  inFlightSlotRequestsRef: { current: Set<string> };
  descriptors: SlotTaskDescriptor[];
  providersWithPendingSlots: Set<string>;
  pendingSlotCountByProvider: Map<string, number>;
}): void => {
  const departmentIds = getProviderDepartmentIds(provider);
  if (departmentIds.length === 0) {
    return;
  }

  const providerNumberId = Number(provider.providerId);
  const schedulingMetadata = buildSchedulingMetadata({
    ageRange,
    clinicianType: provider.clinicianType,
  });

  const providerVisibleIsoDates = getVisibleIsoDatesForProvider(provider.providerId);
  for (const isoDate of providerVisibleIsoDates) {
    const slotRequestKey = `${provider.providerId}:${isoDate}`;
    if (isoDate < minimumSchedulableIsoDate) {
      continue;
    }

    const hasDateAvailability = providerState.availableDateSet.has(isoDate);
    if (
      !hasDateAvailability ||
      providerState.slotsByIsoDate[isoDate] ||
      inFlightSlotRequestsRef.current.has(slotRequestKey)
    ) {
      continue;
    }

    inFlightSlotRequestsRef.current.add(slotRequestKey);
    providersWithPendingSlots.add(provider.providerId);
    pendingSlotCountByProvider.set(
      provider.providerId,
      (pendingSlotCountByProvider.get(provider.providerId) || 0) + 1,
    );

    descriptors.push({
      task: buildProviderSlotTask({
        provider,
        isoDate,
        modalityPreference,
        practiceId,
        providerNumberId,
        departmentIds,
        userUuid: userUuid || "",
        latitude: normalizedLatitude,
        longitude: normalizedLongitude,
        durationMinutes: schedulingMetadata.durationMinutes,
        startHour,
        endHour,
        minimumDayTime: isoDate === minimumSchedulableIsoDate ? minimumDayTime : undefined,
        cacheScope: buildProviderSlotsCacheScope({
          practiceId,
          departmentIds,
          durationMinutes: schedulingMetadata.durationMinutes,
          clinicianType: provider.clinicianType,
          latitude: normalizedLatitude,
          longitude: normalizedLongitude,
          minimumDayTime: isoDate === minimumSchedulableIsoDate ? minimumDayTime : undefined,
        }),
      }),
      slotRequestKey,
      providerId: provider.providerId,
      isoDate,
    });
  }
};

const buildProviderSlotTaskPlan = ({
  visibleProviders,
  availabilityByProvider,
  minimumSchedulableIsoDate,
  minimumDayTime,
  startHour,
  endHour,
  modalityPreference,
  practiceId,
  ageRange,
  userUuid,
  normalizedLatitude,
  normalizedLongitude,
  getVisibleIsoDatesForProvider,
  inFlightSlotRequestsRef,
}: BuildProviderSlotTaskPlanInput): {
  descriptors: SlotTaskDescriptor[];
  providersWithPendingSlots: Set<string>;
  pendingSlotCountByProvider: Map<string, number>;
} => {
  const descriptors: SlotTaskDescriptor[] = [];
  const providersWithPendingSlots = new Set<string>();
  const pendingSlotCountByProvider = new Map<string, number>();

  for (const provider of visibleProviders) {
    const providerState = availabilityByProvider[provider.providerId];
    if (!providerState || providerState.isLoadingDates) {
      continue;
    }

    appendProviderSlotTaskDescriptors({
      provider,
      providerState,
      minimumSchedulableIsoDate,
      minimumDayTime,
      startHour,
      endHour,
      modalityPreference,
      practiceId,
      ageRange,
      userUuid,
      normalizedLatitude,
      normalizedLongitude,
      getVisibleIsoDatesForProvider,
      inFlightSlotRequestsRef,
      descriptors,
      providersWithPendingSlots,
      pendingSlotCountByProvider,
    });
  }

  return {
    descriptors,
    providersWithPendingSlots,
    pendingSlotCountByProvider,
  };
};

const executeProviderSlotTask = async ({
  task,
  slotRequestKey,
  providerId,
  isoDate,
  slotRequestGeneration,
  slotRequestGenerationRef,
  inFlightSlotRequestsRef,
  pendingSlotCountByProvider,
  setAvailabilityByProvider,
  userUuid,
}: ExecuteProviderSlotTaskInput): Promise<void> => {
  let taskResult: { providerId: string; isoDate: string; slots: ProviderSlot[] } | null = null;
  let didTaskFail = false;

  try {
    taskResult = await task();
  } catch (error) {
    didTaskFail = true;
    ClientLogsService.logError(
      userUuid || "unknown",
      "ProviderList.loadAvailabilitySlots",
      `Error loading provider availability slots for provider ${providerId} on ${isoDate}`,
      error instanceof Error ? error : new Error("Unknown error"),
    );
  } finally {
    inFlightSlotRequestsRef.current.delete(slotRequestKey);

    const remainingSlots = Math.max(0, (pendingSlotCountByProvider.get(providerId) || 1) - 1);
    pendingSlotCountByProvider.set(providerId, remainingSlots);

    setAvailabilityByProvider((current) => {
      if (slotRequestGenerationRef.current !== slotRequestGeneration) {
        return current;
      }

      const next = { ...current };
      const providerState = next[providerId] || buildDefaultAvailabilityState();
      let nextProviderState = {
        ...providerState,
      };

      if (taskResult) {
        const nextSlotsByIsoDate = {
          ...providerState.slotsByIsoDate,
          [taskResult.isoDate]: taskResult.slots,
        };

        const nonEmptySlotIsoDates = getNonEmptySlotIsoDates(nextSlotsByIsoDate);

        const shouldAnchorWindowToFirstSlotsDate =
          providerState.dateWindowOffset === 0 &&
          nonEmptySlotIsoDates.length > 0 &&
          providerState.windowStartIsoDate !== nonEmptySlotIsoDates[0];

        nextProviderState = {
          ...nextProviderState,
          slotsByIsoDate: nextSlotsByIsoDate,
          windowStartIsoDate: shouldAnchorWindowToFirstSlotsDate
            ? nonEmptySlotIsoDates[0]
            : providerState.windowStartIsoDate,
        };
      } else if (didTaskFail) {
        // Mark the date as loaded to avoid repeatedly queuing a failing request.
        nextProviderState = {
          ...nextProviderState,
          slotsByIsoDate: {
            ...providerState.slotsByIsoDate,
            [isoDate]: [],
          },
        };
      }

      nextProviderState = {
        ...nextProviderState,
        isLoadingSlots: remainingSlots > 0,
        errorMessage:
          didTaskFail && !nextProviderState.errorMessage
            ? "Failed to load provider time slots."
            : nextProviderState.errorMessage,
      };

      next[providerId] = nextProviderState;
      return next;
    });
  }
};

const buildProviderSlotTask = ({
  provider,
  isoDate,
  modalityPreference,
  practiceId,
  providerNumberId,
  departmentIds,
  userUuid,
  latitude,
  longitude,
  durationMinutes,
  startHour,
  endHour,
  cacheScope,
  minimumDayTime,
}: BuildProviderSlotTaskInput): (() => Promise<{
  providerId: string;
  isoDate: string;
  slots: ProviderSlot[];
}>) => {
  return async () => {
    const careFormats = getCareFormatsForPreference(modalityPreference);
    const mergedSlots: ProviderSlot[] = [];

    for (const careFormat of careFormats) {
      const cachedSlots = getCachedProviderSlots(
        provider.providerId,
        isoDate,
        careFormat,
        cacheScope,
      );
      if (cachedSlots) {
        mergedSlots.push(...cachedSlots);
        continue;
      }

      const response = await getProviderAvailableSlots({
        userUuid,
        careFormat,
        practiceId,
        providerId: providerNumberId,
        departmentIds,
        startDate: formatIsoDateAsUs(isoDate),
        endDate: formatIsoDateAsUs(isoDate),
        userLatitude: latitude,
        userLongitude: longitude,
        minimum_day_time: minimumDayTime,
        duration: durationMinutes,
        clinicianType: provider.clinicianType,
      });

      if (!response.success || !response.data) {
        continue;
      }

      const normalizedSlots = response.data.slots.map((slot) => {
        return {
          ...slot,
          careFormat,
        };
      });

      setCachedProviderSlots(provider.providerId, isoDate, careFormat, cacheScope, normalizedSlots);
      mergedSlots.push(...normalizedSlots);
    }

    const filteredSlots = filterSlotsByHourRange(mergedSlots, startHour, endHour);

    return {
      providerId: provider.providerId,
      isoDate,
      slots: sortSlotsByTime(filteredSlots),
    };
  };
};

const mergeProviderPhotoMaps = (
  currentProviderPhotoMap: Record<string, ProviderPhoto>,
  nextProviderPhotoMap: Record<string, ProviderPhoto>,
): Record<string, ProviderPhoto> => {
  return {
    ...currentProviderPhotoMap,
    ...nextProviderPhotoMap,
  };
};

const updateProviderPhotoLoadingMap = ({
  providers,
  isLoading,
}: {
  providers: ProviderPhotoKey[];
  isLoading: boolean;
}): Record<string, boolean> => {
  return providers.reduce<Record<string, boolean>>((nextProviderPhotoLoadingMap, provider) => {
    nextProviderPhotoLoadingMap[buildProviderPhotoKey(provider)] = isLoading;
    return nextProviderPhotoLoadingMap;
  }, {});
};

const updateProviderPhotoLoadingMapFromCache = ({
  cachedProviderPhotoMap,
  providers,
}: {
  cachedProviderPhotoMap: Record<string, ProviderPhoto>;
  providers: ProviderPhotoKey[];
}): Record<string, boolean> => {
  return providers.reduce<Record<string, boolean>>((nextProviderPhotoLoadingMap, provider) => {
    const providerPhotoKey = buildProviderPhotoKey(provider);
    nextProviderPhotoLoadingMap[providerPhotoKey] = !cachedProviderPhotoMap[providerPhotoKey];
    return nextProviderPhotoLoadingMap;
  }, {});
};

export const ProviderList = ({
  isMobile,
  isTablet = false,
  isSmallerTablet = false,
  isCompactSlotControls = false,
}: {
  isMobile: boolean;
  isTablet?: boolean;
  isSmallerTablet?: boolean;
  isCompactSlotControls?: boolean;
}) => {
  const { primaryFilter } = useFilterContext();
  const { providers, loading, totalProvidersCount, fetchProviders, sortBy, setSortBy } =
    useProviderListContext();
  const {
    FEATURE_SCHEDULING_CONSOLIDATION,
    FEATURE_PROVIDER_AVAILABILITY_BATCH,
    SCHEDULING_CONSOLIDATION_EXPOSURE,
  } = useFeatureFlags();
  const isSchedulingConsolidationEnabled = Boolean(FEATURE_SCHEDULING_CONSOLIDATION);
  const isProviderAvailabilityBatchEnabled = Boolean(FEATURE_PROVIDER_AVAILABILITY_BATCH);
  const getThemePageText = useThemePageText();
  const { activeThemeConfig } = useThemeContext();
  const [pageSize, setPageSize] = useState<number>(5); // Dynamic page size for mobile
  const availabilityDatesPerPage = isMobile || isTablet ? 2 : 3;
  const isMediumMax = useMediaQuery(MEDIA_QUERIES.mediumMax);
  const isWideTabletMax = useMediaQuery(MEDIA_QUERIES.wideTabletMax);

  // Clinic mode requires both the configured provider group and department scope.
  const { clinicProviders, otherProviders, isClinicMode } = useMemo(() => {
    return partitionProvidersByGroupAndDepartment(
      providers,
      activeThemeConfig?.providerGroupId,
      activeThemeConfig?.departmentIds,
    );
  }, [providers, activeThemeConfig?.providerGroupId, activeThemeConfig?.departmentIds]);
  const clinicNameForDepartmentScope = useMemo(
    () => getClinicNameForDepartmentScope(clinicProviders, activeThemeConfig?.departmentIds),
    [clinicProviders, activeThemeConfig?.departmentIds],
  );

  const [isExpanded, setIsExpanded] = useState(false);

  const [providerPhotoMap, setProviderPhotoMap] = useState<Record<string, ProviderPhoto>>({});
  const [providerPhotoLoadingMap, setProviderPhotoLoadingMap] = useState<Record<string, boolean>>(
    {},
  );
  const completedProviderPhotoRequestSignatureRef = useRef<string | null>(null);
  const inFlightProviderPhotoRequestSignatureRef = useRef<string | null>(null);
  const navigate = useNavigateWithUTM();
  const { startTimer, startTimerWithServerTimestamp, stopTimer } = useTimerContext();
  const location = useLocation();
  const { CONTACT_US_WEBFORM_URL } = useEnvironment();
  const {
    data: applicationData,
    setData: setApplicationData,
    markRouteAsComplete,
    clearAppointmentSelection,
  } = useApplicationContext();
  const initialApplicationData = useRef(applicationData);
  const { runExclusive, isPending: isFreezePending } = useExclusiveAction(
    "find-provider:freeze-slot",
  );
  const [availabilityByProvider, setAvailabilityByProvider] = useState<
    Record<string, AvailabilityRequestState>
  >({});
  const [requestedBatchDateOffset, setRequestedBatchDateOffset] = useState(0);
  const [batchDateOffset, setBatchDateOffset] = useState(0);
  const [locationPromptState, setLocationPromptState] = useState<LocationPromptState>(null);
  const availabilityRequestGenerationRef = useRef(0);
  const slotRequestGenerationRef = useRef(0);
  const providerSearchGenerationRef = useRef(0);
  const inFlightSlotRequestsRef = useRef<Set<string>>(new Set());
  // Provider/signature pairs currently owned by a batch-availability request. The signature
  // ownership matters when requests overlap: an older request must not release a newer
  // request's claim for the same provider and allow a duplicate batch or legacy fetch.
  const batchClaimedProviderIdsRef = useRef<Map<string, string>>(new Map());
  // State may be rebuilt when a filter refresh replaces the provider rows. Keep the attempted
  // signature outside that state so the same settled batch is not launched again on rerender.
  const batchAttemptedSignatureByProviderIdRef = useRef<Map<string, string>>(new Map());
  const availabilityJourneyRef = useRef<{
    signature: string;
    startedAtMs: number;
    correlationId: string;
    firstUsableEmitted: boolean;
    completionEmitted: boolean;
    providerSearchGeneration: number;
  } | null>(null);
  const fallbackMonitoringRef = useRef<
    Map<string, { correlationId: string; batchStatus: string; startedAtMs: number }>
  >(new Map());
  // Gates the initial availability fetch until any previously frozen appointment has been
  // released, so a just-freed slot is included in the first fetch instead of appearing
  // unavailable when the user returns to this page (e.g. via the browser Back button).
  const [isUnfreezeComplete, setIsUnfreezeComplete] = useState(false);

  useEffect(() => {
    if (requestedBatchDateOffset === batchDateOffset) {
      return;
    }

    const timeoutId = globalThis.setTimeout(() => {
      setBatchDateOffset(requestedBatchDateOffset);
    }, AVAILABILITY_PAGER_DEBOUNCE_MS);

    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [batchDateOffset, requestedBatchDateOffset]);

  const getLocationOptionsForProvider = useCallback(
    (provider: Provider): DepartmentOption[] => {
      const options = generateDepartmentOptions(provider);
      if (!applicationData.isUnder12InPersonProceed) {
        return options;
      }

      return options.filter((option) => {
        return option.value !== "virtual";
      });
    },
    [applicationData.isUnder12InPersonProceed],
  );

  const proceedWithBooking = useCallback(
    async (
      provider: Provider,
      selectedSlot: { isoDate: string; slot: ProviderSlot },
      selectedDepartment: ProviderDepartment,
    ) => {
      const practiceId = applicationData.address?.context_id;
      if (!practiceId || !applicationData.userUuid) {
        return;
      }

      await runExclusive(async () => {
        const response = await freezeAppointmentSlots({
          userUuid: applicationData.userUuid || "",
          practiceId,
          appointmentIds: selectedSlot.slot.appointment_ids,
          freezeAppointment: true,
        });

        if (!response.success || !response.data) {
          setAvailabilityByProvider((current) => {
            const currentProviderState =
              current[provider.providerId] || buildDefaultAvailabilityState();
            return {
              ...current,
              [provider.providerId]: {
                ...currentProviderState,
                errorMessage:
                  typeof response.error === "string"
                    ? response.error
                    : "That time is no longer available. Please choose another slot.",
              },
            };
          });
          return;
        }

        const selectedProviderPhotoKey =
          provider.providerId && Number.isInteger(provider?.contextId)
            ? buildProviderPhotoKey({
                contextId: provider.contextId,
                providerId: provider.providerId,
              })
            : "";

        const selectedProviderPhoto =
          providerPhotoMap[selectedProviderPhotoKey] || provider.providerPhoto;
        const schedulingMetadata = buildSchedulingMetadata({
          ageRange: applicationData.ageRange,
          clinicianType: provider.clinicianType,
        });

        setApplicationData("selectedProvider", {
          ...provider,
          providerPhoto: getProviderPhotoMetadata(selectedProviderPhoto),
          sessionDuration: schedulingMetadata.sessionDurationMinutes,
        });

        setApplicationData("selectedDate", selectedSlot.isoDate);
        setApplicationData("selectedDepartment", selectedDepartment);

        const appointmentDate = dayjs(selectedSlot.isoDate).format("MM/DD/YYYY");
        setApplicationData("appointmentDetails", {
          providerId: provider.providerId,
          apptDate: appointmentDate,
          apptTime: formatAppointmentTime(selectedSlot.slot.start_time),
          appointmentIds: selectedSlot.slot.appointment_ids,
          appointmentTypeId: selectedSlot.slot.appointment_type_id,
          careFormat:
            selectedDepartment.clinic_name === "Virtual"
              ? CARE_FORMAT.TELEHEALTH
              : CARE_FORMAT.IN_PERSON,
          apptLocation: selectedDepartment,
          userTimezone: applicationData.userTimezone?.name || "patient",
        });

        if (response.data.frozenAt) {
          startTimerWithServerTimestamp(response.data.frozenAt);
        } else {
          startTimer();
        }

        markRouteAsComplete(APP_ROUTES.FIND_PROVIDER);
        markRouteAsComplete(APP_ROUTES.PROVIDER_PROFILE);
        setApplicationData("providerSelectionSource", APP_ROUTES.FIND_PROVIDER);
        setApplicationData("providerBackPath", APP_ROUTES.FIND_PROVIDER);
        navigate(getInitialPatientInformationPath(applicationData.paymentMethod));
      });
    },
    [
      applicationData.address?.context_id,
      applicationData.ageRange,
      applicationData.paymentMethod,
      applicationData.userTimezone?.name,
      applicationData.userUuid,
      markRouteAsComplete,
      navigate,
      providerPhotoMap,
      runExclusive,
      setApplicationData,
      startTimer,
      startTimerWithServerTimestamp,
    ],
  );

  useReleaseFrozenAppointmentOnMount({
    userUuid: initialApplicationData.current.userUuid,
    hasFrozenAppointment: Boolean(
      initialApplicationData.current.appointmentDetails?.appointmentIds?.length,
    ),
    logContext: "ProviderList.unfreezeExistingAppointment",
    onReleased: () => {
      clearAppointmentSelection();
      stopTimer();
    },
    onSettled: () => {
      setIsUnfreezeComplete(true);
    },
  });

  const getVisibleIsoDatesForProvider = useCallback(
    (providerId: string): string[] => {
      const providerState = availabilityByProvider[providerId] || buildDefaultAvailabilityState();
      const displayEligibleIsoDates = filterIsoDatesBySelectedWeekdays(
        sortIsoDates(Array.from(providerState.availableDateSet)),
        applicationData.dayOfWeek,
      );

      // Hide dates that are confirmed empty after slot loading while still showing
      // dates that have not loaded yet so they can be requested/rendered.
      const displayCandidateIsoDates = displayEligibleIsoDates.filter((isoDate) => {
        const loadedSlots = providerState.slotsByIsoDate[isoDate];
        if (!loadedSlots) {
          return true;
        }

        return loadedSlots.length > 0;
      });

      return displayCandidateIsoDates.slice(
        providerState.dateWindowOffset,
        providerState.dateWindowOffset + availabilityDatesPerPage,
      );
    },
    [applicationData.dayOfWeek, availabilityByProvider, availabilityDatesPerPage],
  );

  const getDisplayEligibleIsoDatesForProvider = useCallback(
    (providerId: string): string[] => {
      const providerState = availabilityByProvider[providerId] || buildDefaultAvailabilityState();
      const availableIsoDates = sortIsoDates(Array.from(providerState.availableDateSet));
      const displayEligibleIsoDates = filterIsoDatesBySelectedWeekdays(
        availableIsoDates,
        applicationData.dayOfWeek,
      );

      return displayEligibleIsoDates.filter((isoDate) => {
        const loadedSlots = providerState.slotsByIsoDate[isoDate];
        if (!loadedSlots) {
          return true;
        }

        return loadedSlots.length > 0;
      });
    },
    [applicationData.dayOfWeek, availabilityByProvider],
  );

  const canGoNextDatesForProvider = useCallback(
    (providerId: string): boolean => {
      if (!isSchedulingConsolidationEnabled) {
        return false;
      }

      const providerState = availabilityByProvider[providerId] || buildDefaultAvailabilityState();

      // Keep next enabled while date boundaries are unresolved so paging intent is preserved.
      if (providerState.isLoadingDates || !providerState.datesLoaded) {
        return true;
      }

      const visibleIsoDates = getVisibleIsoDatesForProvider(providerId);
      const currentLastVisibleIsoDate = visibleIsoDates[visibleIsoDates.length - 1];

      if (!currentLastVisibleIsoDate) {
        return false;
      }

      const displayEligibleIsoDates = getDisplayEligibleIsoDatesForProvider(providerId);
      return displayEligibleIsoDates.some((isoDate) => {
        return isDateAfter(isoDate, currentLastVisibleIsoDate);
      });
    },
    [
      availabilityByProvider,
      getDisplayEligibleIsoDatesForProvider,
      getVisibleIsoDatesForProvider,
      isSchedulingConsolidationEnabled,
    ],
  );

  const getProviderAvailabilityStatus = useCallback(
    (providerId: string): ProviderAvailabilityStatus => {
      const providerState = availabilityByProvider[providerId] || buildDefaultAvailabilityState();

      if (providerState.errorMessage) {
        return "error";
      }

      if (
        providerState.isLoadingDates ||
        !providerState.datesLoaded ||
        providerState.isLoadingSlots
      ) {
        return "loading";
      }

      const visibleIsoDates = getVisibleIsoDatesForProvider(providerId);
      const hasUnresolvedVisibleDate = visibleIsoDates.some((isoDate) => {
        return (
          providerState.availableDateSet.has(isoDate) && !providerState.slotsByIsoDate[isoDate]
        );
      });

      if (hasUnresolvedVisibleDate) {
        return "loading";
      }

      return "complete";
    },
    [availabilityByProvider, getVisibleIsoDatesForProvider],
  );

  const modalityPreference = useMemo(() => {
    return resolveModalityFromSessionType(applicationData.sessionType);
  }, [applicationData.sessionType]);
  const hasMixedSessionTypeSelection = useMemo(() => {
    return hasMixedSessionTypes(applicationData.sessionType);
  }, [applicationData.sessionType]);
  // Clinic-priority grouping would break the API's distance order, so skip it while Distance sort is active.
  const isDistanceSortActive = sortBy === PROVIDER_SORT_OPTIONS.NEAREST_TO_FURTHEST;
  const resolveDisplayProviders = useCallback((): Provider[] => {
    if (!isClinicMode || isDistanceSortActive) {
      return providers;
    }

    return isExpanded ? [...clinicProviders, ...otherProviders] : clinicProviders;
  }, [isClinicMode, isDistanceSortActive, isExpanded, clinicProviders, otherProviders, providers]);
  const displayedProviders = useMemo(() => {
    const providersToDisplay = resolveDisplayProviders();
    // Virtual-only providers go last only for Distance sort with both session types selected.
    return hasMixedSessionTypeSelection && isDistanceSortActive
      ? orderVirtualOnlyProvidersLast(providersToDisplay)
      : providersToDisplay;
  }, [resolveDisplayProviders, hasMixedSessionTypeSelection, isDistanceSortActive]);

  useEffect(() => {
    if (
      modalityPreference === "telehealth" &&
      sortBy === PROVIDER_SORT_OPTIONS.NEAREST_TO_FURTHEST
    ) {
      setSortBy(PROVIDER_SORT_OPTIONS.RECOMMENDED);
    }
  }, [modalityPreference, setSortBy, sortBy]);

  const { state, setData, ...paginationProps } = usePagination({
    data: Array.isArray(displayedProviders) ? displayedProviders : [],
    pageSize: isMobile ? pageSize : 5, // Use dynamic page size for mobile
    pages: Math.ceil(
      (Array.isArray(displayedProviders) ? displayedProviders.length : 0) /
        (isMobile ? pageSize : 5),
    ), // Total number of pages
  });

  // Initialize fromScreener flag from location state on first visit, then persist in context
  useEffect(() => {
    if (location.state?.fromScreener && !applicationData.fromScreener) {
      setApplicationData("fromScreener", true);
    }
  }, [location.state?.fromScreener, applicationData.fromScreener, setApplicationData]);

  const shouldNavigateBackToScreener = Boolean(applicationData.fromScreener);
  const findProviderLabels = useResolvedPageLabels("findProvider");
  const noResultsHelpText = getThemePageText(
    "findProvider",
    "noResultsHelpText",
    findProviderLabels.noResultsHelpText,
  );
  const noResultsAppointmentRequestText = getThemePageText(
    "findProvider",
    "noResultsAppointmentRequestText",
    findProviderLabels.noResultsAppointmentRequestText,
  );
  const trimmedAppointmentRequestWebformUrl = CONTACT_US_WEBFORM_URL.trim();
  const appointmentRequestWebformUrl = useMemo(() => {
    return getValidHttpsUrl(trimmedAppointmentRequestWebformUrl);
  }, [trimmedAppointmentRequestWebformUrl]);
  const shouldShowAppointmentRequestCta = applicationData.userRole
    ? WEBFORM_ELIGIBLE_ROLES.has(applicationData.userRole) && Boolean(appointmentRequestWebformUrl)
    : false;
  const noResultsPhoneText = shouldShowAppointmentRequestCta
    ? noResultsAppointmentRequestText
    : noResultsHelpText;

  // Show the expand row when: clinic mode is active, list is not yet expanded, there are
  // non-clinic providers available, and the last clinic provider is currently visible
  // (i.e., the user is on the final page of clinic providers).
  const showExpandRow = useMemo(() => {
    if (
      !isClinicMode ||
      isDistanceSortActive ||
      isExpanded ||
      otherProviders.length === 0 ||
      clinicProviders.length === 0
    ) {
      return false;
    }
    const lastClinicProviderId = clinicProviders[clinicProviders.length - 1]?.providerId;
    return (state.rows as Provider[]).some((p) => {
      return p.providerId === lastClinicProviderId;
    });
  }, [
    isClinicMode,
    isDistanceSortActive,
    isExpanded,
    otherProviders.length,
    clinicProviders,
    state.rows,
  ]);

  useEffect(() => {
    if (!trimmedAppointmentRequestWebformUrl || appointmentRequestWebformUrl) {
      return;
    }

    ClientLogsService.logError(
      applicationData.userUuid || "unknown",
      "ProviderList.validateAppointmentRequestWebformUrl",
      "Invalid Patient Inquiry webform URL configured.",
      new Error("Patient Inquiry webform URL must be a valid HTTPS URL."),
    );
  }, [trimmedAppointmentRequestWebformUrl, appointmentRequestWebformUrl, applicationData.userUuid]);

  const visibleProviderPhotoRequest = useMemo(() => {
    return (state.rows as Provider[])
      .filter((provider) => {
        return provider?.providerId && Number.isInteger(provider?.contextId);
      })
      .map((provider) => {
        return {
          contextId: provider.contextId,
          providerId: String(provider.providerId),
        };
      });
  }, [state.rows]);

  const visibleProviderPhotoRequestSignature = useMemo(() => {
    return [
      applicationData.userUuid || "",
      ...visibleProviderPhotoRequest.map((provider) => {
        return buildProviderPhotoKey(provider);
      }),
    ].join("|");
  }, [applicationData.userUuid, visibleProviderPhotoRequest]);

  // API call function to fetch providers with current filters
  const callProviderSearchApi = useCallback(
    (appData: typeof applicationData, selectedSortBy = sortBy) => {
      return fetchProviders(async () => {
        try {
          const requestedParamData = buildProviderSearchRequest(
            appData,
            getProviderSearchSortOverrides(selectedSortBy, appData),
          );
          const data = await getProviderList(requestedParamData);

          if (data.error) {
            setApplicationData("providerList", []);
            return [];
          }

          const mappedProviders = mapProviderList(data.message ?? []);
          const filteredProviders = filterProvidersByRequestedModality(
            mappedProviders,
            requestedParamData.modality,
          );
          // Virtual-only providers go last only for Distance sort with both session types selected.
          const orderedProviders =
            hasMixedSessionTypes(appData.sessionType) &&
            selectedSortBy === PROVIDER_SORT_OPTIONS.NEAREST_TO_FURTHEST
              ? orderVirtualOnlyProvidersLast(filteredProviders)
              : filteredProviders;
          setApplicationData("providerList", orderedProviders);
          return orderedProviders;
        } catch (error) {
          if ((error as { name?: string })?.name === "AbortError") {
            return [];
          }
          ClientLogsService.logError(
            appData.userUuid || "unknown",
            "ProviderList.callProviderSearchApi",
            "Error fetching provider list",
            error instanceof Error ? error : new Error("Unknown error"),
          );
          setApplicationData("providerList", []);
          return [];
        }
      });
    },
    [fetchProviders, setApplicationData, sortBy],
  );

  const handleSortChange = (newValue: unknown) => {
    const selectedSortBy = String(newValue);
    setSortBy(selectedSortBy);

    if (shouldRefreshProviderSearchForSort(selectedSortBy)) {
      callProviderSearchApi(applicationData, selectedSortBy);
    }
  };

  //  Helper to determine if text should be hidden
  const shouldHideResultsText = (): boolean => {
    return isMobile && (loading || totalProvidersCount === 0);
  };

  // Helper to get results count display value
  const getResultsDisplayValue = (): string => {
    if (isClinicMode && !isDistanceSortActive && !isExpanded && clinicProviders.length > 0) {
      const clinicName =
        clinicNameForDepartmentScope ?? clinicProviders[0]?.providerGroupName?.trim();
      const clinicSuffix = clinicName ? ` from ${clinicName}` : "";
      return `${clinicProviders.length} provider${clinicProviders.length > 1 ? "s" : ""} found${clinicSuffix}`;
    }
    if (totalProvidersCount === 0) {
      return `${totalProvidersCount} providers in ${primaryFilter?.location} meet your criteria`;
    }
    return `${totalProvidersCount} provider${totalProvidersCount > 1 ? "s" : ""} found in ${primaryFilter?.location}`;
  };

  //Handler to load more providers
  const handleLoadMore = () => {
    setPageSize((prevPageSize) => {
      return prevPageSize + 5;
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only — must not re-run when sortBy
  // changes callProviderSearchApi's identity, or it would re-fetch with the stale initial appData
  // instead of the live sort selection.
  useEffect(() => {
    const initialData = initialApplicationData.current;

    if (initialData.providerList?.length) {
      // Providers already cached — show loader while the list is being prepared
      fetchProviders();
      return;
    }

    // No cached providers — fetch from API (loader is owned by fetchProviders inside callProviderSearchApi)
    callProviderSearchApi(initialData);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: paginationProps and setData come from usePagination hook and change when providers change; clinicProviders derives from providers (same dep)
  useEffect(() => {
    providerSearchGenerationRef.current += 1;
    availabilityRequestGenerationRef.current += 1;
    slotRequestGenerationRef.current += 1;
    batchClaimedProviderIdsRef.current.clear();
    batchAttemptedSignatureByProviderIdRef.current.clear();
    inFlightSlotRequestsRef.current.clear();
    fallbackMonitoringRef.current.clear();
    paginationProps.firstPage();
    setIsExpanded(false);
    setData(resolveDisplayProviders());
    setPageSize(5);
    setRequestedBatchDateOffset(0);
    setBatchDateOffset(0);
    setAvailabilityByProvider({});
  }, [providers]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: paginationProps and setData are stable references; clinicProviders and otherProviders derive from providers which is managed by the [providers] effect
  useEffect(() => {
    setData(resolveDisplayProviders());
  }, [isClinicMode, isDistanceSortActive, isExpanded, clinicProviders, otherProviders, providers]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: visibleProviderPhotoRequestSignature fully keys the request payload/order; depending on the array identity can drop in-flight responses.
  useEffect(() => {
    if (visibleProviderPhotoRequest.length === 0) {
      setProviderPhotoLoadingMap({});
      return;
    }

    const cachedProviderPhotoMap = getCachedProviderPhotosByProviderKey(
      visibleProviderPhotoRequest,
    );
    const uncachedVisibleProviderPhotoRequest = visibleProviderPhotoRequest.filter((provider) => {
      return !cachedProviderPhotoMap[buildProviderPhotoKey(provider)];
    });

    setProviderPhotoMap((currentProviderPhotoMap) => {
      return mergeProviderPhotoMaps(currentProviderPhotoMap, cachedProviderPhotoMap);
    });

    if (
      completedProviderPhotoRequestSignatureRef.current === visibleProviderPhotoRequestSignature
    ) {
      setProviderPhotoLoadingMap(() => {
        return updateProviderPhotoLoadingMap({
          providers: visibleProviderPhotoRequest,
          isLoading: false,
        });
      });
      return;
    }

    if (uncachedVisibleProviderPhotoRequest.length === 0) {
      setProviderPhotoLoadingMap(() => {
        return updateProviderPhotoLoadingMap({
          providers: visibleProviderPhotoRequest,
          isLoading: false,
        });
      });
      return;
    }

    if (inFlightProviderPhotoRequestSignatureRef.current === visibleProviderPhotoRequestSignature) {
      setProviderPhotoLoadingMap(() => {
        return updateProviderPhotoLoadingMap({
          providers: uncachedVisibleProviderPhotoRequest,
          isLoading: true,
        });
      });
      return;
    }

    const requestSignature = visibleProviderPhotoRequestSignature;
    inFlightProviderPhotoRequestSignatureRef.current = requestSignature;
    setProviderPhotoLoadingMap(() => {
      return updateProviderPhotoLoadingMapFromCache({
        cachedProviderPhotoMap,
        providers: visibleProviderPhotoRequest,
      });
    });
    getProviderPhotos({
      providers: uncachedVisibleProviderPhotoRequest,
      user_uuid: applicationData.userUuid,
    })
      .then((response) => {
        if (inFlightProviderPhotoRequestSignatureRef.current !== requestSignature) {
          return;
        }

        if (!response.success) {
          return;
        }

        completedProviderPhotoRequestSignatureRef.current = requestSignature;

        setProviderPhotoMap((currentProviderPhotoMap) => {
          return mergeProviderPhotoMaps(
            currentProviderPhotoMap,
            mapProviderPhotosByProviderKey(response.data?.photos ?? []),
          );
        });
      })
      .catch(() => undefined)
      .finally(() => {
        if (inFlightProviderPhotoRequestSignatureRef.current === requestSignature) {
          inFlightProviderPhotoRequestSignatureRef.current = null;
          setProviderPhotoLoadingMap(() => {
            return updateProviderPhotoLoadingMap({
              providers: visibleProviderPhotoRequest,
              isLoading: false,
            });
          });
        }
      });
  }, [visibleProviderPhotoRequestSignature, applicationData.userUuid]);

  const visibleProviders = useMemo(() => {
    return (state.rows as Provider[]).filter((provider) => {
      return Boolean(provider?.providerId);
    });
  }, [state.rows]);

  useFeatureExposure(
    "SCHEDULING_CONSOLIDATION",
    isSchedulingConsolidationEnabled
      ? "consolidated-provider-availability"
      : "standard-provider-results",
    APP_ROUTES.FIND_PROVIDER,
    !loading && visibleProviders.length > 0,
  );

  const visibleProvidersSignature = useMemo(() => {
    return visibleProviders
      .map((provider) => {
        const departmentIds = [...getProviderDepartmentIds(provider)].sort((first, second) => {
          return first - second;
        });
        return `${provider.providerId}:${provider.clinicianType}:${departmentIds.join(",")}`;
      })
      .join("|");
  }, [visibleProviders]);

  const datesRequestSignature = useMemo(() => {
    return [
      applicationData.address?.context_id || "",
      applicationData.ageRange || "",
      (applicationData.dayOfWeek || []).join(","),
      String(applicationData.startHour ?? ""),
      String(applicationData.endHour ?? ""),
      applicationData.userTimezone?.standardName || "",
      applicationData.userUuid || "",
      modalityPreference,
      visibleProvidersSignature,
      String(batchDateOffset),
    ].join("|");
  }, [
    applicationData.address?.context_id,
    applicationData.ageRange,
    applicationData.dayOfWeek,
    applicationData.startHour,
    applicationData.endHour,
    applicationData.userTimezone?.standardName,
    applicationData.userUuid,
    batchDateOffset,
    modalityPreference,
    visibleProvidersSignature,
  ]);

  // Shared by both the batch-availability effect and the legacy per-provider dates effect
  // below — kept as a single implementation so the two stay in sync (Sonar: duplicate-function).
  const getProviderDatesRequestSignature = useCallback(
    (provider: Provider): string => {
      const departmentIds = [...getProviderDepartmentIds(provider)].sort((first, second) => {
        return first - second;
      });
      return `${datesRequestSignature}|${provider.providerId}:${provider.clinicianType}:${departmentIds.join(",")}`;
    },
    [datesRequestSignature],
  );

  // Fetches an initial availability window (dates + slots) for visible providers in one
  // batched call, seeding availabilityByProvider so the legacy per-provider dates/slots
  // effects below skip them. Only ever active when BOTH the consolidation flow's inline
  // per-card availability and the batch-availability flag are enabled — see AGENTS notes:
  // when consolidation is off, no per-card fan-out happens at all (batch flag is then a
  // no-op); when the batch flag is off, this effect no-ops and legacy fan-out is untouched.
  useEffect(() => {
    if (
      !isSchedulingConsolidationEnabled ||
      !isProviderAvailabilityBatchEnabled ||
      loading ||
      visibleProviders.length === 0
    ) {
      return;
    }

    if (!isUnfreezeComplete) {
      return;
    }

    const practiceId = applicationData.address?.context_id;
    if (!practiceId) {
      return;
    }

    if (!applicationData.ageRange) {
      return;
    }

    const providersNeedingBatch = visibleProviders.filter((provider) => {
      const providerState = availabilityByProvider[provider.providerId];
      const providerDatesRequestSignature = getProviderDatesRequestSignature(provider);
      if (
        batchClaimedProviderIdsRef.current.get(provider.providerId) ===
        providerDatesRequestSignature
      ) {
        return false;
      }
      if (
        batchAttemptedSignatureByProviderIdRef.current.get(provider.providerId) ===
        providerDatesRequestSignature
      ) {
        return false;
      }
      const alreadyAttempted =
        providerState?.batchAttemptedSignature === providerDatesRequestSignature;
      if (alreadyAttempted) {
        return false;
      }
      return (
        !providerState ||
        providerState.datesRequestSignature !== providerDatesRequestSignature ||
        (!providerState.datesLoaded && !providerState.isLoadingDates)
      );
    });

    if (providersNeedingBatch.length === 0) {
      return;
    }

    for (const provider of providersNeedingBatch) {
      batchClaimedProviderIdsRef.current.set(
        provider.providerId,
        getProviderDatesRequestSignature(provider),
      );
      batchAttemptedSignatureByProviderIdRef.current.set(
        provider.providerId,
        getProviderDatesRequestSignature(provider),
      );
    }

    // Captured once, at fire time, so the .then/.catch handlers below can detect
    // whether a newer request has since claimed a provider (e.g. filters changed
    // while this call was in flight) and skip applying a now-stale response.
    const requestedSignatureByProviderId = new Map(
      providersNeedingBatch.map((provider) => [
        provider.providerId,
        getProviderDatesRequestSignature(provider),
      ]),
    );
    const journeySignature = Array.from(requestedSignatureByProviderId.values())
      .sort((left, right) => left.localeCompare(right))
      .join("|");
    const providerSearchGeneration = providerSearchGenerationRef.current;
    const previousJourney = availabilityJourneyRef.current;
    if (previousJourney && !previousJourney.completionEmitted) {
      ClientLogsService.logProviderAvailabilityOutcome(
        "provider_availability_terminal",
        previousJourney.correlationId,
        {
          outcome: "cancelled_replaced",
          elapsedMs: Date.now() - previousJourney.startedAtMs,
        },
      );
    }
    const journeyCorrelationId = `availability-${Date.now()}-${providerSearchGeneration}`;
    availabilityJourneyRef.current = {
      signature: journeySignature,
      startedAtMs: Date.now(),
      correlationId: journeyCorrelationId,
      firstUsableEmitted: false,
      completionEmitted: false,
      providerSearchGeneration,
    };

    setAvailabilityByProvider((current) => {
      const next = { ...current };
      for (const provider of providersNeedingBatch) {
        const providerState = next[provider.providerId] || buildDefaultAvailabilityState();
        const providerDatesRequestSignature = getProviderDatesRequestSignature(provider);
        next[provider.providerId] = {
          ...providerState,
          isLoadingDates: true,
          datesRequestSignature: providerDatesRequestSignature,
          batchAttemptedSignature: providerDatesRequestSignature,
          errorMessage: undefined,
          datesLoaded: false,
          isLoadingSlots: false,
          availableDateSet: new Set<string>(),
          slotsByIsoDate: {},
          expandedDateSet: new Set<string>(),
          selectedSlotKey: null,
          selectedSlot: undefined,
          dateWindowOffset: batchDateOffset,
        };
      }
      return next;
    });

    const releaseClaims = () => {
      if (providerSearchGenerationRef.current !== providerSearchGeneration) {
        return;
      }
      for (const provider of providersNeedingBatch) {
        if (
          batchClaimedProviderIdsRef.current.get(provider.providerId) ===
          requestedSignatureByProviderId.get(provider.providerId)
        ) {
          batchClaimedProviderIdsRef.current.delete(provider.providerId);
        }
      }
    };

    getProviderAvailabilityBatch({
      user_uuid: applicationData.userUuid,
      context_id: practiceId,
      modality: modalityPreference,
      user_timezone: applicationData.userTimezone?.standardName,
      ageRange: applicationData.ageRange,
      dateOffset: batchDateOffset,
      monitoring: {
        exposureSource: SCHEDULING_CONSOLIDATION_EXPOSURE?.source ?? "not_exposed",
        configuredPercent: SCHEDULING_CONSOLIDATION_EXPOSURE?.configuredPercent ?? 0,
        rolloutBucket: SCHEDULING_CONSOLIDATION_EXPOSURE?.rolloutBucket ?? null,
        featureVariant: SCHEDULING_CONSOLIDATION_EXPOSURE?.assignedVariant,
        clientCorrelationId: journeyCorrelationId,
      },
      providers: providersNeedingBatch.map((provider) => ({
        providerId: provider.providerId,
        clinicianType: provider.clinicianType,
        departmentIds: getProviderDepartmentIds(provider),
      })),
    })
      .then((response) => {
        if (providerSearchGenerationRef.current !== providerSearchGeneration) {
          return;
        }
        const correlationId = journeyCorrelationId;
        if (response?.error) {
          throw new Error(response.error);
        }

        const availabilityByProviderId = (response?.message ?? {}) as Record<
          string,
          {
            status: "ok" | "partial" | "error";
            dates: string[];
            slotsByIsoDate: Record<string, ProviderSlot[]>;
          }
        >;
        setAvailabilityByProvider((current) => {
          const next = { ...current };
          for (const provider of providersNeedingBatch) {
            const providerState = next[provider.providerId] || buildDefaultAvailabilityState();
            // A newer request already claimed this provider (e.g. filters changed
            // while this call was in flight) — don't overwrite it with a stale result.
            if (
              providerState.datesRequestSignature !==
              requestedSignatureByProviderId.get(provider.providerId)
            ) {
              continue;
            }

            const availability = availabilityByProviderId[provider.providerId];

            if (availability?.status !== "ok") {
              fallbackMonitoringRef.current.set(provider.providerId, {
                correlationId,
                batchStatus: availability?.status ?? "missing",
                startedAtMs: Date.now(),
              });
              // Leave datesLoaded false so the legacy per-provider effect fetches fresh.
              next[provider.providerId] = {
                ...providerState,
                isLoadingDates: false,
              };
              continue;
            }

            next[provider.providerId] = {
              ...providerState,
              isLoadingDates: false,
              datesLoaded: true,
              availableDateSet: new Set(availability.dates),
              slotsByIsoDate: {
                ...providerState.slotsByIsoDate,
                ...availability.slotsByIsoDate,
              },
              // Preserve any user paging action that occurred while dates were loading.
              dateWindowOffset: providerState.dateWindowOffset,
            };
          }
          return next;
        });
      })
      .catch((error) => {
        if (providerSearchGenerationRef.current !== providerSearchGeneration) {
          return;
        }
        // Expected when a newer batch request supersedes this one (latest-wins
        // policy); the newer request already owns this provider's state.
        if ((error as { name?: string })?.name === "AbortError") {
          return;
        }

        ClientLogsService.logError(
          applicationData.userUuid || "unknown",
          "ProviderList.loadAvailabilityBatch",
          "Error loading provider availability batch",
          error instanceof Error ? error : new Error("Unknown error"),
        );

        const correlationId = journeyCorrelationId;
        for (const provider of providersNeedingBatch) {
          fallbackMonitoringRef.current.set(provider.providerId, {
            correlationId,
            batchStatus: "request_error",
            startedAtMs: Date.now(),
          });
        }

        setAvailabilityByProvider((current) => {
          const next = { ...current };
          for (const provider of providersNeedingBatch) {
            const providerState = next[provider.providerId];
            if (!providerState) {
              continue;
            }
            if (
              providerState.datesRequestSignature !==
              requestedSignatureByProviderId.get(provider.providerId)
            ) {
              continue;
            }
            next[provider.providerId] = {
              ...providerState,
              isLoadingDates: false,
            };
          }
          return next;
        });
      })
      .finally(releaseClaims);
  }, [
    applicationData.address?.context_id,
    applicationData.ageRange,
    applicationData.userTimezone?.standardName,
    applicationData.userUuid,
    availabilityByProvider,
    batchDateOffset,
    getProviderDatesRequestSignature,
    isProviderAvailabilityBatchEnabled,
    isSchedulingConsolidationEnabled,
    isUnfreezeComplete,
    loading,
    modalityPreference,
    SCHEDULING_CONSOLIDATION_EXPOSURE,
    visibleProviders,
  ]);

  useEffect(() => {
    const journey = availabilityJourneyRef.current;
    if (
      !journey ||
      journey.providerSearchGeneration !== providerSearchGenerationRef.current ||
      visibleProviders.length === 0
    ) {
      return;
    }

    const outcomes = visibleProviders.map((provider) => {
      const state = availabilityByProvider[provider.providerId];
      const hasUsableSlots = Boolean(
        state && Object.values(state.slotsByIsoDate).some((slots) => slots.length > 0),
      );
      const isError = Boolean(
        state?.datesLoaded && !state.isLoadingDates && !state.isLoadingSlots && state.errorMessage,
      );
      const isNoAvailability = Boolean(
        state?.datesLoaded &&
          !state.isLoadingDates &&
          !state.isLoadingSlots &&
          !state.errorMessage &&
          !hasUsableSlots &&
          filterIsoDatesBySelectedWeekdays(
            Array.from(state.availableDateSet),
            applicationData.dayOfWeek,
          ).every((isoDate) => {
            return Object.hasOwn(state.slotsByIsoDate, isoDate);
          }),
      );
      return { hasUsableSlots, isError, isNoAvailability };
    });

    if (!journey.firstUsableEmitted && outcomes.some((outcome) => outcome.hasUsableSlots)) {
      journey.firstUsableEmitted = true;
      ClientLogsService.logProviderAvailabilityOutcome(
        "provider_availability_first_usable",
        journey.correlationId,
        {
          elapsedMs: Date.now() - journey.startedAtMs,
          exposureSource: SCHEDULING_CONSOLIDATION_EXPOSURE?.source ?? "unknown",
          configuredPercent: SCHEDULING_CONSOLIDATION_EXPOSURE?.configuredPercent ?? -1,
          rolloutBucket: SCHEDULING_CONSOLIDATION_EXPOSURE?.rolloutBucket ?? -1,
        },
      );
    }

    const allSettled = outcomes.every(
      (outcome) => outcome.hasUsableSlots || outcome.isError || outcome.isNoAvailability,
    );
    if (allSettled && !journey.completionEmitted) {
      journey.completionEmitted = true;
      ClientLogsService.logProviderAvailabilityOutcome(
        "provider_availability_all_settled",
        journey.correlationId,
        {
          elapsedMs: Date.now() - journey.startedAtMs,
          visibleProviderCount: outcomes.length,
          usableProviderCount: outcomes.filter((outcome) => outcome.hasUsableSlots).length,
          noAvailabilityProviderCount: outcomes.filter((outcome) => outcome.isNoAvailability)
            .length,
          errorProviderCount: outcomes.filter((outcome) => outcome.isError).length,
          exposureSource: SCHEDULING_CONSOLIDATION_EXPOSURE?.source ?? "unknown",
        },
      );
    }
  }, [
    applicationData.dayOfWeek,
    availabilityByProvider,
    SCHEDULING_CONSOLIDATION_EXPOSURE,
    visibleProviders,
  ]);

  useEffect(() => {
    return () => {
      const journey = availabilityJourneyRef.current;
      if (journey && !journey.completionEmitted) {
        ClientLogsService.logProviderAvailabilityOutcome(
          "provider_availability_terminal",
          journey.correlationId,
          { outcome: "cancelled_unmounted", elapsedMs: Date.now() - journey.startedAtMs },
        );
        journey.completionEmitted = true;
      }
    };
  }, []);

  useEffect(() => {
    if (!isSchedulingConsolidationEnabled || loading || visibleProviders.length === 0) {
      return;
    }

    // Wait until any previously frozen appointment is released so its slot is reflected
    // as available in this initial availability fetch.
    if (!isUnfreezeComplete) {
      return;
    }

    const practiceId = applicationData.address?.context_id;
    if (!practiceId) {
      return;
    }

    // ageRange drives durationMinutes and the cache key — wait until it's set to avoid
    // a wasted fetch with the wrong duration followed immediately by a re-fetch.
    if (!applicationData.ageRange) {
      return;
    }

    const providersNeedingDates = visibleProviders.filter((provider) => {
      if (batchClaimedProviderIdsRef.current.has(provider.providerId)) {
        return false;
      }

      const providerState = availabilityByProvider[provider.providerId];
      const providerDatesRequestSignature = getProviderDatesRequestSignature(provider);
      return (
        providerState?.datesRequestSignature !== providerDatesRequestSignature ||
        (!providerState?.datesLoaded && !providerState?.isLoadingDates)
      );
    });

    if (providersNeedingDates.length === 0) {
      return;
    }

    const userTimezone = applicationData.userTimezone?.standardName;
    const currentMoment = userTimezone ? moment().tz(userTimezone) : moment();
    const minimumAppointmentDateTime = getMinimumAppointmentMoment(
      new Date(),
      userTimezone,
      applicationData.userRole,
    );
    const minimumSchedulableIsoDate = minimumAppointmentDateTime
      .clone()
      .startOf("day")
      .format("YYYY-MM-DD");
    const startDate = minimumAppointmentDateTime.clone().startOf("day").format("MM/DD/YYYY");

    const requestGeneration = availabilityRequestGenerationRef.current + 1;
    availabilityRequestGenerationRef.current = requestGeneration;

    setAvailabilityByProvider((current) => {
      const next = { ...current };
      for (const provider of providersNeedingDates) {
        const providerState = next[provider.providerId] || buildDefaultAvailabilityState();
        next[provider.providerId] = {
          ...providerState,
          isLoadingDates: true,
          datesRequestSignature: getProviderDatesRequestSignature(provider),
          errorMessage: undefined,
        };
      }
      return next;
    });

    const tasks = providersNeedingDates.map((provider) => {
      const departmentIds = getProviderDepartmentIds(provider);
      const schedulingMetadata = buildSchedulingMetadata({
        ageRange: applicationData.ageRange,
        clinicianType: provider.clinicianType,
      });
      const endDateBase =
        provider.clinicianType === CLINICIAN_TYPE.PRESCRIBER
          ? currentMoment.clone().startOf("day")
          : minimumAppointmentDateTime.clone().startOf("day");
      const endDate = endDateBase
        .add(Math.min(30, schedulingMetadata.schedulingWindowDays), "days")
        .format("MM/DD/YYYY");

      return buildProviderDatesTask({
        provider,
        practiceId,
        startDate,
        currentMoment,
        minimumAppointmentDateTime,
        modalityPreference,
        ageRange: applicationData.ageRange,
        userUuid: applicationData.userUuid,
        userTimezone: applicationData.userTimezone?.standardName,
        cacheScope: buildProviderDatesCacheScope({
          practiceId,
          departmentIds,
          startDate,
          endDate,
          minimumSchedulableIsoDate,
          durationMinutes: schedulingMetadata.durationMinutes,
          clinicianType: provider.clinicianType,
          userTimezone: applicationData.userTimezone?.standardName,
        }),
      });
    });

    asyncPool(3, tasks)
      .then((results) => {
        if (availabilityRequestGenerationRef.current !== requestGeneration) {
          return;
        }

        const dateTaskResultsByProvider = results.map((result, index) => {
          return {
            providerId: providersNeedingDates[index]?.providerId,
            result,
          };
        });

        setAvailabilityByProvider((current) => {
          const next = { ...current };

          for (const entry of dateTaskResultsByProvider) {
            if (!entry.providerId) {
              continue;
            }

            if (entry.result.status === "rejected") {
              const providerState = next[entry.providerId] || buildDefaultAvailabilityState();
              next[entry.providerId] = {
                ...providerState,
                isLoadingDates: false,
                datesLoaded: true,
                errorMessage: "Failed to load provider availability dates.",
              };
              continue;
            }

            const providerId = entry.result.value.providerId;
            const providerState = next[providerId] || buildDefaultAvailabilityState();

            const nextAvailableDateSet = new Set(
              getSchedulableIsoDates(entry.result.value.isoDates, minimumSchedulableIsoDate),
            );
            const firstAvailableIsoDate = sortIsoDates(Array.from(nextAvailableDateSet))[0];

            const updatedState = {
              ...providerState,
              isLoadingDates: false,
              datesLoaded: true,
              errorMessage: entry.result.value.errorMessage,
              availableDateSet: nextAvailableDateSet,
              windowStartIsoDate: firstAvailableIsoDate || minimumSchedulableIsoDate,
              // Preserve any user paging action that occurred while dates were loading.
              dateWindowOffset: providerState.dateWindowOffset,
            };

            next[providerId] = updatedState;
          }

          return next;
        });

        for (const entry of dateTaskResultsByProvider) {
          if (!entry.providerId) {
            continue;
          }

          logProviderDatesTaskOutcome({
            userUuid: applicationData.userUuid,
            providerId: entry.providerId,
            minimumSchedulableIsoDate,
            result:
              entry.result.status === "rejected"
                ? { status: "rejected", reason: entry.result.reason }
                : { status: "fulfilled", value: entry.result.value },
          });

          const fallbackMonitoring = fallbackMonitoringRef.current.get(entry.providerId);
          if (fallbackMonitoring) {
            let fallbackOutcome = "failed";
            if (entry.result.status === "fulfilled") {
              fallbackOutcome =
                entry.result.value.isoDates.length > 0 ? "succeeded" : "no_availability";
            }
            ClientLogsService.logProviderAvailabilityOutcome(
              "provider_availability_fallback",
              fallbackMonitoring.correlationId,
              {
                providerId: entry.providerId,
                attempted: true,
                outcome: fallbackOutcome,
                originatingBatchStatus: fallbackMonitoring.batchStatus,
                recoveryElapsedMs: Date.now() - fallbackMonitoring.startedAtMs,
              },
            );
            fallbackMonitoringRef.current.delete(entry.providerId);
          }
        }
      })
      .catch((error) => {
        ClientLogsService.logError(
          applicationData.userUuid || "unknown",
          "ProviderList.loadAvailabilityDates",
          "Error loading provider availability dates",
          error instanceof Error ? error : new Error("Unknown error"),
        );
      });
  }, [
    applicationData.address?.context_id,
    applicationData.ageRange,
    applicationData.userTimezone?.standardName,
    applicationData.userRole,
    applicationData.userUuid,
    availabilityByProvider,
    getProviderDatesRequestSignature,
    isSchedulingConsolidationEnabled,
    isUnfreezeComplete,
    loading,
    modalityPreference,
    visibleProviders,
  ]);

  useEffect(() => {
    if (!isSchedulingConsolidationEnabled) {
      return;
    }

    if (visibleProviders.length === 0) {
      return;
    }

    const practiceId = applicationData.address?.context_id;
    const latitude = applicationData.address?.position?.lat;
    const longitude = applicationData.address?.position?.lon;
    const userTimezone = applicationData.userTimezone?.standardName;
    const minimumAppointmentDateTime = getMinimumAppointmentMoment(
      new Date(),
      userTimezone,
      applicationData.userRole,
    );
    const minimumSchedulableIsoDate = minimumAppointmentDateTime
      .clone()
      .startOf("day")
      .format("YYYY-MM-DD");
    const minimumDayTime = minimumAppointmentDateTime.format("YYYY-MM-DDTHH:mm:ssZ");

    if (!practiceId) {
      return;
    }

    if (latitude == null || longitude == null) {
      return;
    }

    const slotTaskPlan = buildProviderSlotTaskPlan({
      visibleProviders,
      availabilityByProvider,
      minimumSchedulableIsoDate,
      minimumDayTime,
      startHour: applicationData.startHour,
      endHour: applicationData.endHour,
      modalityPreference,
      practiceId,
      ageRange: applicationData.ageRange,
      userUuid: applicationData.userUuid,
      normalizedLatitude: latitude,
      normalizedLongitude: longitude,
      getVisibleIsoDatesForProvider,
      inFlightSlotRequestsRef,
    });

    const { descriptors, providersWithPendingSlots, pendingSlotCountByProvider } = slotTaskPlan;

    if (descriptors.length === 0) {
      return;
    }

    const slotRequestGeneration = slotRequestGenerationRef.current + 1;
    slotRequestGenerationRef.current = slotRequestGeneration;

    const tasks = descriptors.map((descriptor) => {
      return async () => {
        await executeProviderSlotTask({
          task: descriptor.task,
          slotRequestKey: descriptor.slotRequestKey,
          providerId: descriptor.providerId,
          isoDate: descriptor.isoDate,
          slotRequestGeneration,
          slotRequestGenerationRef,
          inFlightSlotRequestsRef,
          pendingSlotCountByProvider,
          setAvailabilityByProvider,
          userUuid: applicationData.userUuid,
        });
      };
    });

    setAvailabilityByProvider((current) => {
      const next = { ...current };
      for (const providerId of providersWithPendingSlots) {
        const providerState = next[providerId] || buildDefaultAvailabilityState();
        next[providerId] = {
          ...providerState,
          isLoadingSlots: true,
        };
      }
      return next;
    });

    asyncPool(4, tasks)
      .catch((error) => {
        ClientLogsService.logError(
          applicationData.userUuid || "unknown",
          "ProviderList.loadAvailabilitySlots",
          "Error loading provider availability slots",
          error instanceof Error ? error : new Error("Unknown error"),
        );
        setAvailabilityByProvider((current) => {
          const next = { ...current };
          for (const providerId of providersWithPendingSlots) {
            const providerState = next[providerId] || buildDefaultAvailabilityState();
            next[providerId] = {
              ...providerState,
              isLoadingSlots: false,
              errorMessage: "Failed to load provider time slots.",
            };
          }
          return next;
        });
      })
      .finally(() => {
        if (slotRequestGenerationRef.current !== slotRequestGeneration) {
          return;
        }

        // Clear any lingering in-flight markers after this batch completes.
        for (const provider of visibleProviders) {
          for (const isoDate of getVisibleIsoDatesForProvider(provider.providerId)) {
            inFlightSlotRequestsRef.current.delete(`${provider.providerId}:${isoDate}`);
          }
        }
      });
  }, [
    applicationData.address?.context_id,
    applicationData.address?.position?.lat,
    applicationData.address?.position?.lon,
    applicationData.ageRange,
    applicationData.startHour,
    applicationData.endHour,
    applicationData.userTimezone?.standardName,
    applicationData.userRole,
    applicationData.userUuid,
    availabilityByProvider,
    isSchedulingConsolidationEnabled,
    modalityPreference,
    visibleProviders,
    getVisibleIsoDatesForProvider,
  ]);

  const onPreviousProviderDates = (providerId: string) => {
    setAvailabilityByProvider((current) => {
      const providerState = current[providerId] || buildDefaultAvailabilityState();
      return {
        ...current,
        [providerId]: {
          ...providerState,
          dateWindowOffset: Math.max(0, providerState.dateWindowOffset - availabilityDatesPerPage),
        },
      };
    });
  };

  const onNextProviderDates = (providerId: string) => {
    setAvailabilityByProvider((current) => {
      const providerState = current[providerId] || buildDefaultAvailabilityState();
      return {
        ...current,
        [providerId]: {
          ...providerState,
          dateWindowOffset: providerState.dateWindowOffset + availabilityDatesPerPage,
        },
      };
    });
  };

  const canGoPreviousAllProviderDates = requestedBatchDateOffset > 0;
  const canGoNextAllProviderDates = visibleProviders.some((provider) => {
    return (
      getDisplayEligibleIsoDatesForProvider(provider.providerId).length >
      requestedBatchDateOffset + availabilityDatesPerPage
    );
  });

  const onPreviousAllProviderDates = () => {
    setRequestedBatchDateOffset((current) => {
      return Math.max(0, current - availabilityDatesPerPage);
    });
  };

  const onNextAllProviderDates = () => {
    setRequestedBatchDateOffset((current) => current + availabilityDatesPerPage);
  };

  const onToggleDateExpanded = (providerId: string, isoDate: string) => {
    setAvailabilityByProvider((current) => {
      const providerState = current[providerId] || buildDefaultAvailabilityState();
      const nextExpandedDateSet = new Set(providerState.expandedDateSet);

      if (nextExpandedDateSet.has(isoDate)) {
        nextExpandedDateSet.delete(isoDate);
      } else {
        nextExpandedDateSet.add(isoDate);
      }

      return {
        ...current,
        [providerId]: {
          ...providerState,
          expandedDateSet: nextExpandedDateSet,
        },
      };
    });
  };

  const onSelectAvailabilitySlot = ({
    providerId,
    isoDate,
    slot,
    modalityLabel,
  }: AvailabilitySelection) => {
    if (isTestRecordBlocked(applicationData)) {
      return;
    }

    setAvailabilityByProvider((current) => {
      const next = { ...current };
      for (const pid of Object.keys(next)) {
        if (pid !== providerId) {
          next[pid] = { ...next[pid], selectedSlotKey: null, selectedSlot: undefined };
        }
      }
      const providerState = next[providerId] || buildDefaultAvailabilityState();
      next[providerId] = {
        ...providerState,
        selectedSlotKey: buildAvailabilitySlotKey(providerId, isoDate, slot),
        selectedSlot: { isoDate, slot, modalityLabel },
      };
      return next;
    });
  };

  const onScheduleSelectedTime = async (providerId: string) => {
    // Salesforce test leads may not freeze or schedule appointments.
    if (isTestRecordBlocked(applicationData)) {
      setAvailabilityByProvider((current) => {
        const currentProviderState = current[providerId] || buildDefaultAvailabilityState();
        return {
          ...current,
          [providerId]: {
            ...currentProviderState,
            errorMessage: TEST_RECORD_BLOCK_MESSAGE,
          },
        };
      });
      return;
    }

    const provider = visibleProviders.find((item) => {
      return item.providerId === providerId;
    });
    const providerState = availabilityByProvider[providerId];
    const selectedSlot = providerState?.selectedSlot;

    if (
      !provider ||
      !selectedSlot ||
      !applicationData.userUuid ||
      !applicationData.address?.context_id
    ) {
      return;
    }

    const practiceId = applicationData.address?.context_id;
    if (!practiceId) {
      return;
    }

    const options = getLocationOptionsForProvider(provider);
    const shouldShowLocationModal =
      selectedSlot.modalityLabel === MODALITY_LABEL.VIRTUAL_AND_IN_PERSON ||
      selectedSlot.modalityLabel === MODALITY_LABEL.IN_PERSON;

    if (!shouldShowLocationModal) {
      let selectedDepartment: ProviderDepartment;

      if (selectedSlot.modalityLabel === MODALITY_LABEL.VIRTUAL) {
        // Use virtual department for virtual-only sessions
        selectedDepartment = {
          department_id: -1,
          clinic_name: "Virtual",
          address_line_1: "",
          address_line_2: "",
          city: "",
          state: "",
          zip_code: "",
          department_latitude: 0,
          department_longitude: 0,
        };
      } else {
        // For other cases, find matching department or use first one
        selectedDepartment =
          provider.departments.find((department) => {
            return department.department_id === selectedSlot.slot.department_id;
          }) || provider.departments[0];
      }

      if (!selectedDepartment) {
        return;
      }

      await proceedWithBooking(provider, selectedSlot, selectedDepartment);
      return;
    }

    const physicalOptions = options.filter((option) => {
      return option.value !== "virtual";
    });
    const modalOptions =
      selectedSlot.modalityLabel === MODALITY_LABEL.VIRTUAL_AND_IN_PERSON
        ? options
        : physicalOptions;
    // Base the default exclusively on choices the user can actually see. This avoids
    // auto-selecting a physical location when the modal also offers virtual care.
    const initialValue = modalOptions.length === 1 ? modalOptions[0].value : "";

    setLocationPromptState({
      provider,
      selectedSlot,
      options: modalOptions,
      initialValue,
    });
  };

  const handleLocationModalConfirm = useCallback(
    (value: string) => {
      if (!locationPromptState) {
        return;
      }

      const selectedOption = locationPromptState.options.find((option) => {
        return option.value === value;
      });

      if (!selectedOption) {
        return;
      }

      setLocationPromptState(null);
      void proceedWithBooking(
        locationPromptState.provider,
        locationPromptState.selectedSlot,
        selectedOption.department,
      );
    },
    [locationPromptState, proceedWithBooking],
  );

  const handleLocationModalClose = useCallback(() => {
    setLocationPromptState(null);
  }, []);

  const renderLoadingState = () => {
    return (
      <Card>
        <LoadingSpinnerContainer>
          <LoadingSpinner
            size="xl"
            ariaLoadingLabel="Providers Loading"
            isLoading={loading}
            color="$app.color.provider.list.loading.spinner"
          />
          <LoadingTextContainer>
            <Heading css={{ fontSize: "25px" }}>Please Wait</Heading>
            <Text css={{ fontSize: "14px" }}>while we retrieve available providers.</Text>
          </LoadingTextContainer>
        </LoadingSpinnerContainer>
      </Card>
    );
  };

  const renderNoResultsState = () => {
    return (
      <NoResultsContainer isMobile={isMobile}>
        {isMobile && (
          <Divider
            css={{
              "abyss-divider-root": {
                marginTop: "16px !important",
                marginBottom: "24px !important",
              },
            }}
          />
        )}
        <SearchAgain
          headingText="No matches based on your filtered criteria"
          subText={
            <SubTextContainer isMobile={isMobile}>
              <ResultSubText isMobile={isMobile}>
                Try removing one or more filters to see available providers.
              </ResultSubText>
              <ResultSubParagraph isMobile={isMobile}>
                {renderPhoneTemplateText(noResultsPhoneText, PHONE_NUMBER, renderBoldPhoneNumber)}
              </ResultSubParagraph>
            </SubTextContainer>
          }
          isMobile={isMobile}
          secondaryCta={
            shouldShowAppointmentRequestCta
              ? {
                  label: "Request Appointment",
                  id: "obc-find-provider-request-appointment-btn",
                  dataObcLabel: "Find Provider Request Appointment Button",
                  onClick: () => {
                    if (!appointmentRequestWebformUrl) {
                      return;
                    }

                    window.open(appointmentRequestWebformUrl, "_blank", "noopener,noreferrer");
                  },
                }
              : undefined
          }
        />
      </NoResultsContainer>
    );
  };

  const renderFewResultsNotice = () => {
    if (totalProvidersCount > 5) return null;
    return (
      <FewResultsContainer isMobile={isMobile}>
        <Divider
          css={{
            "abyss-divider-root": {
              marginTop: "16px !important",
              marginBottom: "24px !important",
              height: "1px !important",
            },
          }}
        />
        <SearchAgain
          headingText="Adjust Your Filters to Unlock More Matches"
          subText={
            <SubTextContainer isMobile={isMobile}>
              <ResultSubText isMobile={isMobile}>
                Only a few providers match your current filters. Adjusting them could help you find
                more available care.
              </ResultSubText>
            </SubTextContainer>
          }
          isMobile={isMobile}
          isSearchAgain={false}
        />
      </FewResultsContainer>
    );
  };

  const renderProviders = () => {
    const providersToRender = state.rows as Array<Provider & { id?: string }>;

    return (
      <Fragment>
        {providersToRender.map((provider: Provider & { id?: string }, index) => {
          const providerId = String(provider.providerId ?? provider.id ?? "").trim();
          const providerPhotoKey =
            providerId && Number.isInteger(provider?.contextId)
              ? buildProviderPhotoKey({
                  contextId: provider.contextId,
                  providerId,
                })
              : "";
          const providerAvailability =
            availabilityByProvider[provider.providerId] || buildDefaultAvailabilityState();
          const visibleIsoDates = getVisibleIsoDatesForProvider(provider.providerId);
          const providerAvailabilityStatus = getProviderAvailabilityStatus(provider.providerId);
          const providerCardKey =
            providerPhotoKey ||
            providerId ||
            `${provider.fullName || "provider"}:${provider.primaryDepartmentId ?? "unknown"}:${index}`;
          return (
            <ProviderCard
              key={providerCardKey}
              isMobile={isMobile}
              isCompactAvailability={isMobile || isTablet}
              isStackedAvailability={isMobile || isSmallerTablet}
              isMobileAvailability={isCompactSlotControls}
              provider={provider}
              providerPhoto={providerPhotoMap[providerPhotoKey] || provider.providerPhoto}
              providerPhotoIsLoading={Boolean(providerPhotoLoadingMap[providerPhotoKey])}
              availability={
                isSchedulingConsolidationEnabled
                  ? {
                      visibleIsoDates,
                      availableDateSet: providerAvailability.availableDateSet,
                      slotsByIsoDate: providerAvailability.slotsByIsoDate,
                      expandedDateSet: providerAvailability.expandedDateSet,
                      isLoadingDates: providerAvailability.isLoadingDates,
                      isLoadingSlots: providerAvailability.isLoadingSlots,
                      errorMessage: providerAvailability.errorMessage,
                      status: providerAvailabilityStatus,
                      hasNoFilteredAvailability:
                        providerAvailabilityStatus === "complete" && visibleIsoDates.length === 0,
                      selectedSlotKey: providerAvailability.selectedSlotKey,
                      isSubmittingSelection: isFreezePending,
                      isSchedulingBlocked: isTestRecordBlocked(applicationData),
                      canGoPreviousDates: providerAvailability.dateWindowOffset > 0,
                      canGoNextDates: canGoNextDatesForProvider(provider.providerId),
                    }
                  : undefined
              }
              onSelectAvailabilitySlot={
                isSchedulingConsolidationEnabled ? onSelectAvailabilitySlot : undefined
              }
              onToggleDateExpanded={
                isSchedulingConsolidationEnabled ? onToggleDateExpanded : undefined
              }
              onScheduleSelectedTime={
                isSchedulingConsolidationEnabled ? onScheduleSelectedTime : undefined
              }
              onPreviousAvailabilityDates={
                isSchedulingConsolidationEnabled ? onPreviousProviderDates : undefined
              }
              onNextAvailabilityDates={
                isSchedulingConsolidationEnabled ? onNextProviderDates : undefined
              }
            />
          );
        })}
        {renderFewResultsNotice()}
        {showExpandRow && (
          <ExpandProvidersRow
            label={"See more providers near you"}
            onClick={() => {
              return setIsExpanded(true);
            }}
          />
        )}
        {!isMobile && displayedProviders.length > 5 && (
          <Pagination
            {...paginationProps}
            css={{
              "abyss-pagination-root-container": {
                placeItems: "center",
                transform: "translateX(-10px)", // Move slightly to the left
              },
            }}
          />
        )}
        {isMobile && pageSize < displayedProviders.length && (
          <Button
            data-testid="test-id-provider-list-load-more-button"
            variant="outline"
            onClick={handleLoadMore}
            css={{ width: "100%" }}
          >
            Load more
          </Button>
        )}
        <BackButtonHolder data-testid="test-id-find-provider-back-button" isMobile={isMobile}>
          <Button
            data-testid="test-id-provider-list-back-button"
            {...(!isMobile && {
              icon: { icon: "chevron_backward", position: "leading" },
            })}
            size={isMobile ? "lg" : "md"}
            variant={isMobile ? "text" : "outline"}
            onClick={() => {
              navigate(shouldNavigateBackToScreener ? APP_ROUTES.SCREENER : APP_ROUTES.GET_STARTED);
            }}
          >
            Back
          </Button>
        </BackButtonHolder>
      </Fragment>
    );
  };

  const renderResultBlock = () => {
    if (loading) return renderLoadingState();
    if (totalProvidersCount === 0) return renderNoResultsState();
    return renderProviders();
  };

  return (
    <Fragment>
      {/*Additional Active Filters*/}
      <ActiveFilters
        isMobile={isMobile}
        isMediumMax={isMediumMax}
        isWideTabletMax={isWideTabletMax}
        onProviderSearchRefresh={callProviderSearchApi}
      />
      {/* Start of Holder for total number of providers returned and Sort dropdown*/}
      <ResultsHeaderRow
        data-testid="test-id-total-providers-and-sort-by-holder"
        isMobile={isMobile}
      >
        <Text
          css={{
            marginTop: "16px",
            order: isMobile ? 2 : 1,
            display: shouldHideResultsText() ? "none" : "block",
            visibility: loading ? "hidden" : "visible",
          }}
        >
          {getResultsDisplayValue()}
        </Text>
        <SortDropdownHolder data-testid="test-id-sort-dropdown-holder" isMobile={isMobile}>
          <SortLabelText isMobile={isMobile}>Sort by:</SortLabelText>
          <SelectInput
            aria-label="Sort by"
            label=""
            data-testid="test-id-sort-dropdown"
            css={getSortSelectCss(isMobile)}
            value={sortBy}
            onChange={handleSortChange}
            options={getProviderSortSelectOptions(modalityPreference)}
          />
        </SortDropdownHolder>
      </ResultsHeaderRow>
      {/* End of Holder for total number of providers returned and Sort dropdown*/}

      {isSchedulingConsolidationEnabled &&
        isProviderAvailabilityBatchEnabled &&
        totalProvidersCount > 0 && (
          <AvailabilityToolbarContainer data-testid="test-id-availability-toolbar-container">
            <GlobalAvailabilityPager data-testid="test-id-global-availability-pager">
              <GlobalAvailabilityPagerButton
                type="button"
                aria-label="Previous availability dates for all providers"
                id="obc-provider-availability-prev-dates-global"
                data-testid="test-id-provider-availability-prev-dates-global"
                data-obc-label="Provider Availability Previous Dates for all providers"
                disabled={!canGoPreviousAllProviderDates}
                onClick={onPreviousAllProviderDates}
              >
                <IconSymbol icon="chevron_left" size="36" renderMode="svg" />
              </GlobalAvailabilityPagerButton>
              <GlobalAvailabilityPagerButton
                type="button"
                aria-label="Next availability dates for all providers"
                id="obc-provider-availability-next-dates-global"
                data-testid="test-id-provider-availability-next-dates-global"
                data-obc-label="Provider Availability Next Dates for all providers"
                disabled={!canGoNextAllProviderDates}
                onClick={onNextAllProviderDates}
              >
                <IconSymbol icon="chevron_right" size="36" renderMode="svg" />
              </GlobalAvailabilityPagerButton>
            </GlobalAvailabilityPager>
          </AvailabilityToolbarContainer>
        )}

      <LocationSelectionModal
        isOpen={Boolean(locationPromptState)}
        providerName={locationPromptState?.provider.fullName || ""}
        providerType={locationPromptState?.provider.providerType}
        appointmentDate={locationPromptState?.selectedSlot?.isoDate}
        startTime={locationPromptState?.selectedSlot?.slot.start_time}
        options={locationPromptState?.options || []}
        initialValue={locationPromptState?.initialValue || ""}
        onConfirm={handleLocationModalConfirm}
        onClose={handleLocationModalClose}
      />
      {/* Start of holder for provider cards */}
      <ProviderCardsHolder
        data-testid="test-id-provider-cards-holder"
        isMobile={isMobile}
        isSchedulingConsolidationEnabled={isSchedulingConsolidationEnabled}
      >
        {renderResultBlock()}
      </ProviderCardsHolder>

      {/* End of holder for provider cards */}
    </Fragment>
  );
};
